import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { masterAPI, ticketsAPI, usersAPI, groupsAPI, conversationsAPI, slaAPI, activitiesAPI, SERVER_URL } from "../../services/api";
import { formatInTz } from "../../utils/timezone";
import {
  getTicketListingState,
  isTerminalTicketStatus,
  TICKET_STATE_STYLES,
} from "../../utils/ticketListingState";
import { toast } from "../../utils/toast";
import CreateTicketModal from "../../modals/CreateTicketModal";
import InlineAssignedToDropdown from "../tickets/InlineAssignedToDropdown";
import InlineMasterDropdown from "../tickets/InlineMasterDropdown";
import InlineStatusDropdown from "../tickets/InlineStatusDropdown";
import InlinePriorityDropdown from "../tickets/InlinePriorityDropdown";
import TicketTimer, { formatMsShort, getLiveElapsedMs } from "../../ui/TicketTimer";
import { buildTicketUrl } from "../../config/publicUrl";

// Recipient tag input with autocomplete — renders dropdown via portal so it is
// never clipped by overflow:hidden/auto parent containers.
function RecipientInputField({ value, onChange, onAddTag, onRemoveLastTag, placeholder, suggestions }) {
  const inputRef = useRef(null);
  const [show, setShow] = useState(false);
  const [pos,  setPos]  = useState({ top: 0, left: 0, width: 240 });
  const updatePos = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 2, left: r.left, width: Math.max(r.width, 240) });
    }
  };
  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && value.trim()) {
      e.preventDefault();
      onAddTag(value.trim().replace(/,$/, ''));
      onChange('');
      setShow(false);
    } else if (e.key === 'Backspace' && !value) {
      onRemoveLastTag();
    }
  };
  return (
    <div style={{ position: 'relative', flex: '1 1 80px' }}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={e => { onChange(e.target.value); updatePos(); setShow(true); }}
        onKeyDown={handleKeyDown}
        onFocus={() => { updatePos(); if (value) setShow(true); }}
        onBlur={() => setTimeout(() => setShow(false), 160)}
        placeholder={placeholder}
        style={{ flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: '#1A202C', outline: 'none', fontFamily: 'inherit', minWidth: 60, width: '100%' }}
      />
      {show && suggestions.length > 0 && createPortal(
        <div style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 8px 28px rgba(0,0,0,0.15)', zIndex: 99999, maxHeight: 220, overflowY: 'auto' }}>
          {suggestions.slice(0, 8).map(u => (
            <div key={u.id || u.email}
              onMouseDown={e => { e.preventDefault(); onAddTag(u.email); onChange(''); setShow(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid #f1f5f9', background: '#fff' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#6366f1', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {(u.name || u.email)[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#1a202c', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.email}</div>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function useWindowSizeSafe() {
  const [size, setSize] = useState(typeof window !== "undefined" ? window.innerWidth : 1024);
  useEffect(() => {
    const handleResize = () => setSize(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return size;
}

const T = {
  sidebar: "#FFFFFF",
  active: "#3B5BDB",
  bg: "#F0F2F5",
  surface: "#ffffff",
  border: "#DDE1E7",
  border2: "#C8CDD6",
  color: "#1A202C",
  txt2: "#3B5BDB",
  muted: "#718096",
  g50: "#EBF0FF",
  g100: "#E0E7FF",
  g200: "#C7D2FE",
  g700: "#2C41A8",
  txt: "#ffffff",
};

const LISTING_TYPES = ["All", "Products", "Services", "Assets", "Software", "Contracts"];
const STATUS_OPTIONS = ["Open", "Pending Action", "Pending Approval", "Pending User Acceptance", "Completed", "Rejected", "Voided"];
const PRIORITY_FILTER_OPTIONS = ["Low", "Medium", "High", "Urgent", "Critical"];
const TERMINAL_STATUSES = ["Completed", "Rejected", "Voided", "Resolved", "Closed"];
const RESOLVED_STATUSES = ["Resolved", "Closed", "Completed", "Pending User Acceptance"];

function isOverdueTicket(l) {
  // Include completed/closed tickets that breached SLA (matches dashboard breached count)
  if (l.slaStatus === 'breached') return true;
  const raw = l.resolutionDueAt || l.due_by;
  if (!raw) return false;
  const due = new Date(raw);
  if (Number.isNaN(due.getTime())) return false;
  return due < new Date() && !TERMINAL_STATUSES.includes(l.status);
}

function isDueTodayTicket(l) {
  const raw = l.resolutionDueAt || l.due_by;
  if (!raw) return false;
  const due = new Date(raw);
  if (Number.isNaN(due.getTime())) return false;
  const now = new Date();
  return due.getFullYear() === now.getFullYear()
    && due.getMonth() === now.getMonth()
    && due.getDate() === now.getDate()
    && !TERMINAL_STATUSES.includes(l.status);
}
const CATEGORY_OPTIONS = ["Hardware", "Software", "Service", "Subscription", "License", "Contract"];

const DEPARTMENT_OPTIONS = ["IT", "HR", "Finance", "Engineering", "Marketing", "Sales", "Operations", "Legal", "Design", "Support"];

const SORT_FIELDS = [
  { key: "id", label: "Request Number" },
  { key: "requestor", label: "Requestor" },
  { key: "dept", label: "Department" },
  { key: "orgUnit", label: "Organization Unit" },
  { key: "headOfDept", label: "Head of Department" },
  { key: "category", label: "Category" },
  { key: "subCategory", label: "Sub-Category" },
  { key: "additionalSubCategory", label: "Additional Sub-Category" },
  { key: "name", label: "Problem Description" },
  { key: "status", label: "Current Status" },
  { key: "created", label: "Requested At" },
  { key: "modified", label: "Last Update At" },
  { key: "assignedTo", label: "Current Assignee" },
  { key: "firstResponseTime", label: "First Response Time" },
  { key: "requestRating", label: "Request Rating" },
];

const EXPORT_FIELDS = [
  { key: "id", label: "Request Number" },
  { key: "requestor", label: "Requestor" },
  { key: "dept", label: "Department" },
  { key: "orgUnit", label: "Organization Unit" },
  { key: "headOfDept", label: "Head of Department" },
  { key: "category", label: "Category" },
  { key: "subCategory", label: "Sub-Category" },
  { key: "additionalSubCategory", label: "Additional Sub-Category" },
  { key: "name", label: "Problem Description" },
  { key: "status", label: "Current Status" },
  { key: "created", label: "Requested At" },
  { key: "modified", label: "Last Update At" },
  { key: "actionTaker", label: "Action Taker" },
  { key: "actionTaker1", label: "Action Taker 1" },
  { key: "actionTaker1Remarks", label: "Action Taker 1 Remarks" },
  { key: "actionTaker1Timestamp", label: "Action Taker 1 Timestamp" },
  { key: "actionTaker2", label: "Action Taker 2" },
  { key: "actionTaker2Remarks", label: "Action Taker 2 Remarks" },
  { key: "actionTaker2Timestamp", label: "Action Taker 2 Timestamp" },
  { key: "actionTaker3", label: "Action Taker 3" },
  { key: "actionTaker3Remarks", label: "Action Taker 3 Remarks" },
  { key: "actionTaker3Timestamp", label: "Action Taker 3 Timestamp" },
  { key: "requestRating", label: "Request Rating" },
  { key: "remarks", label: "Remarks" },
  { key: "procurementCompletionDate", label: "Procurement Completion Date" },
  { key: "firstResponseTime", label: "First Response Time" },
  { key: "reasonForBreach", label: "Reason for Breach" },
  { key: "endUser", label: "End User" },
  { key: "assignedTo", label: "Current Assignee" },
];

function Icon({ name, size = 16, color = "currentColor" }) {
  const s = { width: size, height: size, minWidth: size, display: "block", flexShrink: 0 };
  const p = { fill: "none", viewBox: "0 0 24 24", stroke: color, strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" };
  const icons = {
    grid: <svg style={s} {...p}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>,
    list: <svg style={s} {...p}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>,
    search: <svg style={s} {...p}><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>,
    plus: <svg style={s} {...p}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
    filter: <svg style={s} {...p}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
    download: <svg style={s} {...p}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    edit: <svg style={s} {...p}><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
    trash: <svg style={s} {...p}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2" /></svg>,
    close: <svg style={s} {...p}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
    chevronD: <svg style={s} {...p}><path d="M6 9l6 6 6-6" /></svg>,
    chevronU: <svg style={s} {...p}><path d="M18 15l-6-6-6 6" /></svg>,
    chevronR: <svg style={s} {...p}><path d="M9 18l6-6-6-6" /></svg>,
    chevronL: <svg style={s} {...p}><path d="M15 18l-6-6 6-6" /></svg>,
    checkMark: <svg style={s} {...p}><polyline points="20 6 9 17 4 12" /></svg>,
    star: <svg style={s} {...p}><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>,
    starFill: <svg style={s} viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>,
    info: <svg style={s} {...p}><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
    moreH: <svg style={s} {...p}><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" /></svg>,
    copy: <svg style={s} {...p}><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" /></svg>,
    tag: <svg style={s} {...p}><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg>,
    user: <svg style={s} {...p}><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    monitor: <svg style={s} {...p}><rect x="2" y="4" width="20" height="14" rx="2" ry="2" /><path d="M8 20h8M12 18v2" /></svg>,
    layers: <svg style={s} {...p}><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>,
    settings: <svg style={s} {...p}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>,
    eye: <svg style={s} {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    calendar: <svg style={s} {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>,
    warning: <svg style={s} {...p}><path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>,
    mail: <svg style={s} {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
    phone: <svg style={s} {...p}><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.72 19.79 19.79 0 01.14 1.1 2 2 0 012.11 0h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 14.92v2z" /></svg>,
  };

  return icons[name] || null;
}

function StatusBadge({ status }) {
  const colors = {
    'Open':                    { bg: '#EBF0FF', color: '#3B5BDB' },
    'Pending Action':          { bg: '#FFFBEB', color: '#B45309' },
    'Pending Approval':        { bg: '#FFF7ED', color: '#C2410C' },
    'Pending User Acceptance': { bg: '#F5F3FF', color: '#6D28D9' },
    'Completed':               { bg: '#ECFDF5', color: '#059669' },
    'Rejected':                { bg: '#FFF1F2', color: '#BE123C' },
    'Voided':                  { bg: '#F8FAFC', color: '#64748B' },
  };
  const c = colors[status] || { bg: '#F8FAFC', color: '#64748B' };
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: "5px 12px", borderRadius: 99, whiteSpace: "nowrap", display: "inline-block", lineHeight: 1.3 }}>
      {status}
    </span>
  );
}

function TypeBadge({ type }) {
  const icons = {
    Products: "monitor",
    Services: "settings",
    Assets: "tag",
    Software: "layers",
    Contracts: "copy",
  };
  const colors = {
    Products: "#3B5BDB",
    Services: "#8b5cf6",
    Assets: "#f59e0b",
    Software: "#3B5BDB",
    Contracts: "#ef4444",
  };
  const icon = icons[type] || "tag";
  const color = colors[type] || "#94a3b8";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      <Icon name={icon} size={12} color={color} />
      <span style={{ fontSize: 11, color: "#718096" }}>{type}</span>
    </div>
  );
}

function Avatar({ name, size = 28 }) {
  const initials = name ? name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() : "?";
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "#C7D2FE", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 700, color: "#14532d", flexShrink: 0 }}>
      {initials}
    </div>
  );
}

function ToolbarFilterSelect({ label, value, onChange, options, minWidth = 130 }) {
  const active = value && value !== "All";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, color: T.muted, fontWeight: 600, whiteSpace: "nowrap" }}>{label}:</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          height: 32,
          minWidth,
          maxWidth: 200,
          padding: "0 8px",
          borderRadius: 7,
          border: `1px solid ${active ? T.active : T.border}`,
          background: active ? T.g50 : "#fff",
          fontSize: 13,
          fontWeight: active ? 700 : 500,
          color: "#1A202C",
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function SortByDropdown({ sortKey, sortDir, onSortChange }) {
  const [open, setOpen] = useState(false);
  const [fieldSearch, setFieldSearch] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const currentLabel = SORT_FIELDS.find(f => f.key === sortKey)?.label || "Requested At";
  const filteredFields = SORT_FIELDS.filter(f => f.label.toLowerCase().includes(fieldSearch.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} style={{ height: 32, padding: "0 12px", borderRadius: 7, border: `1px solid ${open ? T.active : T.border}`, background: open ? T.g50 : "transparent", fontSize: 13, color: "#1A202C", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, transition: "all 0.15s" }}>
        <span style={{ color: T.muted }}>Sort by:</span>
        <span style={{ fontWeight: 700, color: "#1A202C" }}>{currentLabel}</span>
        <span style={{ fontSize: 10, color: T.muted, fontWeight: 600 }}>{sortDir === "asc" ? "↑" : "↓"}</span>
        <Icon name={open ? "chevronU" : "chevronD"} size={13} color={T.muted} />
      </button>

      {open && (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 100, background: "#FFFFFF", border: `1.5px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(5,46,22,0.12)", width: 220, overflow: "hidden" }}>
          <div style={{ padding: "10px 10px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, background: T.g50, borderRadius: 7, padding: "7px 10px", border: `1px solid ${T.border}` }}>
              <Icon name="search" size={13} color={T.muted} />
              <input autoFocus value={fieldSearch} onChange={e => setFieldSearch(e.target.value)} placeholder="Search fields" style={{ border: "none", background: "transparent", fontSize: 12, color: "#1A202C", outline: "none", width: "100%", fontFamily: "inherit" }} />
            </div>
          </div>
          <div style={{ maxHeight: 190, overflowY: "auto" }}>
            {filteredFields.map(f => {
              const isActive = sortKey === f.key;
              return (
                <button key={f.key} onClick={() => { onSortChange(f.key, sortDir); setFieldSearch(""); }} style={{ width: "100%", padding: "9px 14px", border: "none", background: isActive ? T.g50 : "transparent", fontSize: 13, color: isActive ? T.active : "#2D3748", cursor: "pointer", textAlign: "left", display: "flex", alignItems: "center", justifyContent: "space-between", fontWeight: isActive ? 700 : 400 }}>
                  {f.label}
                  {isActive && <Icon name="checkMark" size={14} color={T.active} />}
                </button>
              );
            })}
          </div>
          <div style={{ borderTop: `1.5px solid ${T.border}`, padding: "6px 8px" }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", padding: "4px 6px 6px" }}>Order</div>
            {[{ label: "Ascending", val: "asc", icon: "↑" }, { label: "Descending", val: "desc", icon: "↓" }].map(({ label, val, icon }) => {
              const isSelected = sortDir === val;
              return (
                <button key={val} onClick={() => { onSortChange(sortKey, val); setOpen(false); }} style={{ width: "100%", padding: "8px 10px", border: "none", background: isSelected ? T.g100 : "transparent", fontSize: 13, color: isSelected ? T.active : "#2D3748", cursor: "pointer", textAlign: "left", borderRadius: 6, display: "flex", alignItems: "center", gap: 8, fontWeight: isSelected ? 700 : 400, marginBottom: 2 }}>
                  <span style={{ fontSize: 13, color: isSelected ? T.active : T.muted, width: 14, textAlign: "center" }}>{icon}</span>
                  {label}
                  {isSelected && <Icon name="checkMark" size={14} color={T.active} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ListingModal({ listing, onClose, onSave }) {
  const size = useWindowSizeSafe();
  const isMobile = size < 768;
  const isEdit = !!listing;
  const [form, setForm] = useState(listing || {
    name: "", type: "Incident", category: "", subCategory: "", status: "Open",
    cost: "", assignedTo: "", group: "", dept: "", description: "", expiry: "",
    requestor: "", email: "", _requester_id: null, _assigned_to_id: null, _assigned_group_id: null,
  });
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Modal-level lookup data
  const [modalMaster, setModalMaster] = useState({});
  const [modalGroups, setModalGroups] = useState([]);
  const [modalAgents, setModalAgents] = useState([]);
  useEffect(() => {
    masterAPI.all().then(setModalMaster).catch(() => {});
    groupsAPI.list().then(d => setModalGroups(d || [])).catch(() => {});
    usersAPI.list().then(d => setModalAgents((d || []).filter(u => (u.role || '').toLowerCase() !== 'requester'))).catch(() => {});
  }, []);
  const subCatOptions = (modalMaster.sub_category || []).filter(s => !form.category || s.parent_value === form.category);

  // Requester search
  const [requesters, setRequesters] = useState([]);
  const [requesterSearch, setRequesterSearch] = useState("");
  const [showRequesterDropdown, setShowRequesterDropdown] = useState(false);
  useEffect(() => {
    usersAPI.list()
      .then(data => setRequesters((data || []).filter(u => (u.role || '').toLowerCase() === 'requester')))
      .catch(() => {});
  }, []);
  const filteredRequesters = (requesters || []).filter(u => {
    const q = requesterSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  }).slice(0, 20);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); if (errors[k]) setErrors(e => ({ ...e, [k]: false })); };
  const fieldStyle = key => ({
    width: "100%", padding: "10px 13px", border: `1.5px solid ${errors[key] ? "#C53030" : focused === key ? T.active : T.border}`,
    borderRadius: 8, fontSize: 13, color: "#1A202C", background: focused === key ? "#fff" : T.g50,
    outline: "none", fontFamily: "inherit", boxSizing: "border-box", transition: "border-color 0.15s, background 0.15s",
    boxShadow: focused === key ? `0 0 0 3px ${T.g100}` : "none",
  });
  const lStyle = { fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 6 };

  const handleSave = async () => {
    const errs = {};
    if (!form.name.trim()) errs.name = true;
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    setSaveError("");
    try {
      const now = formatInTz(new Date(), { day: "2-digit", month: "short", year: "numeric" });
      if (isEdit) {
        // Edit: update backend
        if (listing._id) {
          await ticketsAPI.update(listing._id, {
            subject: form.name,
            description: form.description || undefined,
            status: form.status || undefined,
            priority: form.priority || undefined,
            type: form.type || undefined,
            department: form.dept || undefined,
            category: form.category || undefined,
            sub_category: form.subCategory || undefined,
            requester_id: form._requester_id || undefined,
            requester_name: form.requestor || undefined,
            requester_email: form.email || undefined,
            assigned_to: form._assigned_to_id || undefined,
            group_id: form._assigned_group_id || undefined,
          });
        }
        onSave({ ...form, created: listing?.created || now });
      } else {
        // Create: call backend API so workflow engine fires
        const ticket = await ticketsAPI.create({
          subject: form.name.trim(),
          description: form.description || "",
          priority: form.priority || "Low",
          type: form.type || "Incident",
          source: "Portal",
          status: "Open",
          department: form.dept || undefined,
          category: form.category || undefined,
          sub_category: form.subCategory || undefined,
          assigned_to: form._assigned_to_id || undefined,
          group_id: form._assigned_group_id || undefined,
        });
        // Map API response to listing format
        const fmt = (iso) => iso ? formatInTz(iso, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : now;
        onSave({
          _id: ticket.id,
          id: ticket.ticket_number || `#${ticket.id}`,
          name: ticket.subject || form.name,
          description: ticket.description || "",
          status: ticket.status || "Open",
          priority: ticket.priority || "Low",
          type: ticket.type || "Incident",
          source: ticket.source || "Portal",
          category: ticket.category || form.category || "",
          subCategory: ticket.sub_category || "",
          dept: ticket.dept || ticket.department || form.dept || "",
          requestor: ticket.requester || "",
          assignedTo: ticket.assignedTo || "",
          _assigned_to_id: ticket._assigned_to_id || null,
          _assigned_group_id: ticket._assigned_group_id || null,
          group: ticket.group || "",
          vendor: form.vendor || "",
          created: fmt(ticket.created_at),
          modified: fmt(ticket.updated_at),
          slaStatus: null,
        });
      }
    } catch (err) {
      setSaveError(err.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const SectionDivider = ({ label }) => (
    <div style={{ gridColumn: isMobile ? "auto" : "span 2", display: "flex", alignItems: "center", gap: 10, margin: "4px 0 2px" }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: T.active, textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap" }}>{label}</span>
      <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg, ${T.border}, transparent)` }} />
    </div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(5,46,22,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(2px)" }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: "#FFFFFF", borderRadius: 16, width: isMobile ? "95%" : 580, maxHeight: "92vh", display: "flex", flexDirection: "column", boxShadow: "0 32px 80px rgba(5,46,22,0.22), 0 0 0 1px rgba(5,46,22,0.06)", overflow: "hidden" }}>
        <div style={{ padding: "20px 24px 18px", borderBottom: `1px solid ${T.border}`, background: `linear-gradient(135deg, ${T.g50} 0%, #fff 60%)`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: T.active, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(22,163,74,0.35)" }}>
                <Icon name="list" size={16} color="#fff" />
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: "#1A202C", lineHeight: 1.2 }}>{isEdit ? "Edit Listing" : "Create New Listing"}</div>
                <div style={{ fontSize: 11, color: T.muted, marginTop: 1 }}>{isEdit ? `Editing #${listing.id}` : "Fill in the details to add a new listing"}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ width: 30, height: 30, border: `1px solid ${T.border}`, borderRadius: 7, background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} onMouseEnter={e => e.currentTarget.style.background = T.g50} onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <Icon name="close" size={14} color={T.muted} />
            </button>
          </div>
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: isMobile ? "16px" : "20px 24px" }}>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "14px 16px" }}>
            <SectionDivider label="Basic Info" />
            <div style={{ gridColumn: isMobile ? "auto" : "span 2" }}>
              <label style={lStyle}>Name <span style={{ color: "#ef4444" }}>*</span>{errors.name && <span style={{ color: "#ef4444", fontSize: 10, marginLeft: 4 }}>Required</span>}</label>
              <input style={{ ...fieldStyle("name"), ...(errors.name ? { borderColor: "#C53030", boxShadow: "0 0 0 3px #fee2e2" } : {}) }} value={form.name} onChange={e => set("name", e.target.value)} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Listing name" />
            </div>

            <div>
              <label style={lStyle}>Type</label>
              <select style={fieldStyle("type")} value={form.type} onChange={e => set("type", e.target.value)} onFocus={() => setFocused("type")} onBlur={() => setFocused(null)}>
                {["Incident", "Service Request", "Problem", "Change"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Priority</label>
              <select style={fieldStyle("priority")} value={form.priority || "Low"} onChange={e => set("priority", e.target.value)} onFocus={() => setFocused("priority")} onBlur={() => setFocused(null)}>
                {["Low", "Medium", "High", "Urgent"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Category</label>
              <select style={fieldStyle("category")} value={form.category} onChange={e => { set("category", e.target.value); set("subCategory", ""); }} onFocus={() => setFocused("category")} onBlur={() => setFocused(null)}>
                <option value="">-- Select --</option>
                {(modalMaster.category || CATEGORY_OPTIONS.map(v => ({ value: v }))).map(o => <option key={o.value || o} value={o.value || o}>{o.value || o}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Sub-Category</label>
              <select style={{ ...fieldStyle("subCategory"), opacity: form.category ? 1 : 0.5, cursor: form.category ? 'pointer' : 'not-allowed' }} value={form.subCategory || ""} disabled={!form.category} onChange={e => set("subCategory", e.target.value)} onFocus={() => setFocused("subCategory")} onBlur={() => setFocused(null)} title={!form.category ? 'Select a category first' : undefined}>
                <option value="">-- Select --</option>
                {subCatOptions.map(o => <option key={o.id || o.value} value={o.value}>{o.value}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Status</label>
              <select style={fieldStyle("status")} value={form.status} onChange={e => set("status", e.target.value)} onFocus={() => setFocused("status")} onBlur={() => setFocused(null)}>
                {STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
              </select>
            </div>

            <div style={{ gridColumn: isMobile ? "auto" : "span 2", position: "relative" }}>
              <label style={lStyle}>Requester</label>
              {form.requestor && !showRequesterDropdown ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1.5px solid ${T.border}`, borderRadius: 8, background: T.g50 }}>
                  <Avatar name={form.requestor} size={28} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#1A202C", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{form.requestor}</div>
                    {form.email && <div style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{form.email}</div>}
                  </div>
                  <button type="button" onClick={() => { setForm(f => ({ ...f, requestor: "", email: "", _requester_id: null })); setRequesterSearch(""); setShowRequesterDropdown(true); }} style={{ width: 24, height: 24, border: "none", borderRadius: 6, background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }} title="Change requester">
                    <Icon name="close" size={12} color={T.muted} />
                  </button>
                </div>
              ) : (
                <>
                  <input
                    style={fieldStyle("requester")}
                    value={requesterSearch}
                    onChange={e => { setRequesterSearch(e.target.value); setShowRequesterDropdown(true); }}
                    onFocus={() => { setFocused("requester"); setShowRequesterDropdown(true); }}
                    onBlur={() => { setFocused(null); setTimeout(() => setShowRequesterDropdown(false), 180); }}
                    placeholder="Search a requester by name or email"
                  />
                  {showRequesterDropdown && filteredRequesters.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, marginTop: 4, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", maxHeight: 240, overflowY: "auto", zIndex: 10 }}>
                      {filteredRequesters.map(u => (
                        <div
                          key={u.id}
                          onMouseDown={e => e.preventDefault()}
                          onClick={() => {
                            setForm(f => ({ ...f, requestor: u.name || "", email: u.email || "", _requester_id: u.id }));
                            setRequesterSearch("");
                            setShowRequesterDropdown(false);
                          }}
                          style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", cursor: "pointer", borderBottom: `1px solid ${T.g50}` }}
                          onMouseEnter={e => e.currentTarget.style.background = T.g50}
                          onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                        >
                          <Avatar name={u.name || u.email || "?"} size={28} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1A202C", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.name || "—"}</div>
                            <div style={{ fontSize: 11, color: T.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{u.email}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <SectionDivider label="Assignment & Cost" />
            <div>
              <label style={lStyle}>Group</label>
              <select style={fieldStyle("group")} value={form._assigned_group_id || ""} onChange={e => {
                const grp = modalGroups.find(g => String(g.id) === e.target.value);
                set("_assigned_group_id", e.target.value ? Number(e.target.value) : null);
                set("group", grp ? grp.name : "");
              }} onFocus={() => setFocused("group")} onBlur={() => setFocused(null)}>
                <option value="">-- No Group --</option>
                {modalGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Agent</label>
              <select style={fieldStyle("assignedTo")} value={form._assigned_to_id || ""} onChange={e => {
                const agent = modalAgents.find(a => String(a.id) === e.target.value);
                set("_assigned_to_id", e.target.value ? Number(e.target.value) : null);
                set("assignedTo", agent ? agent.name : "");
              }} onFocus={() => setFocused("assignedTo")} onBlur={() => setFocused(null)}>
                <option value="">-- Unassigned --</option>
                {modalAgents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Department</label>
              <select style={fieldStyle("dept")} value={form.dept} onChange={e => set("dept", e.target.value)} onFocus={() => setFocused("dept")} onBlur={() => setFocused(null)}>
                <option value="">-- Select --</option>
                {(modalMaster.department || DEPARTMENT_OPTIONS.map(v => ({ value: v }))).map(o => <option key={o.value || o} value={o.value || o}>{o.value || o}</option>)}
              </select>
            </div>

            <div>
              <label style={lStyle}>Cost</label>
              <input style={fieldStyle("cost")} value={form.cost} onChange={e => set("cost", e.target.value)} onFocus={() => setFocused("cost")} onBlur={() => setFocused(null)} placeholder="e.g. $1,200" />
            </div>

            <div>
              <label style={lStyle}>Expiry Date</label>
              <input type="date" style={fieldStyle("expiry")} value={form.expiry} onChange={e => set("expiry", e.target.value)} onFocus={() => setFocused("expiry")} onBlur={() => setFocused(null)} />
            </div>

            <SectionDivider label="Description" />
            <div style={{ gridColumn: isMobile ? "auto" : "span 2" }}>
              <label style={lStyle}>Description</label>
              <textarea style={{ ...fieldStyle("description"), minHeight: 80, resize: "vertical" }} value={form.description} onChange={e => set("description", e.target.value)} onFocus={() => setFocused("description")} onBlur={() => setFocused(null)} placeholder="Brief description of this listing..." />
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 24px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", gap: 10, background: T.g50, flexShrink: 0, alignItems: "center" }}>
          {saveError && <span style={{ flex: 1, fontSize: 12, color: "#dc2626", fontWeight: 600 }}>{saveError}</span>}
          <button onClick={onClose} disabled={saving} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${T.border}`, background: "#FFFFFF", fontSize: 13, color: T.muted, cursor: "pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: saving ? "#a7f3d0" : T.active, fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", boxShadow: "0 2px 8px rgba(22,163,74,0.3)", display: "flex", alignItems: "center", gap: 6 }}>
            {saving ? "Creating..." : isEdit ? "Save Changes" : "Create Request"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ExportModal({ listings, onClose }) {
  const size = useWindowSizeSafe();
  const isMobile = size < 768;
  const [format, setFormat] = useState("CSV");
  const [selected, setSelected] = useState(new Set(["name", "status", "vendor", "cost", "created"]));

  const toggleField = key => setSelected(s => { const next = new Set(s); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const allSelected = EXPORT_FIELDS.every(f => selected.has(f.key));
  const someSelected = selected.size > 0 && !allSelected;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(EXPORT_FIELDS.map(f => f.key)));

  const handleExport = () => {
    const cols = EXPORT_FIELDS.filter(f => selected.has(f.key));
    if (!cols.length) return;
    const header = cols.map(c => c.label);
    const rows = listings.map(l => cols.map(c => {
      const v = l[c.key] ?? "";
      const safe = typeof v === "string" && v.includes(",") ? `"${v}"` : v;
      return safe;
    }));
    const csv = [header, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `listings_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    onClose();
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 300, display: "flex", justifyContent: "flex-end" }}>
      <div style={{ background: "#FFFFFF", width: isMobile ? "100%" : 460, height: "100%", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #DDE1E7", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1A202C" }}>Export Listings</div>
          <button onClick={onClose} style={{ width: 28, height: 28, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="close" size={18} color="#718096" />
          </button>
        </div>

        <div style={{ padding: "20px 24px", flex: 1, overflowY: "auto" }}>
          <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 14, color: "#2D3748", minWidth: 100 }}>Export as :</span>
            <div style={{ display: "flex", gap: 20 }}>
              {["CSV", "Excel"].map(f => (
                <label key={f} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14, color: "#2D3748" }}>
                  <div onClick={() => setFormat(f)} style={{ width: 18, height: 18, borderRadius: "50%", border: `2px solid ${format === f ? "#2563eb" : "#C8CDD6"}`, background: "#FFFFFF", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
                    {format === f && <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#2563eb" }} />}
                  </div>
                  {f}
                </label>
              ))}
            </div>
          </div>

          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#1A202C" }}>Select fields <span style={{ color: "#ef4444" }}>*</span></span>
              <span style={{ fontSize: 13, color: "#718096" }}>{selected.size} selected</span>
            </div>
            <div onClick={toggleAll} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}>
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${allSelected || someSelected ? "#2563eb" : "#C8CDD6"}`, background: allSelected ? "#2563eb" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {allSelected && <Icon name="checkMark" size={12} color="#fff" />}
                {someSelected && !allSelected && <div style={{ width: 10, height: 2, background: "#2563eb", borderRadius: 1 }} />}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500, color: "#1A202C" }}>Select All</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr" }}>
              {EXPORT_FIELDS.map(f => (
                <div key={f.key} onClick={() => toggleField(f.key)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", cursor: "pointer", borderBottom: "1px solid #f3f4f6" }}>
                  <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selected.has(f.key) ? "#2563eb" : "#C8CDD6"}`, background: selected.has(f.key) ? "#2563eb" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {selected.has(f.key) && <Icon name="checkMark" size={12} color="#fff" />}
                  </div>
                  <span style={{ fontSize: 13, color: "#2D3748" }}>{f.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid #DDE1E7", display: "flex", gap: 12, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "10px 20px", borderRadius: 6, border: "1px solid #C8CDD6", background: "#FFFFFF", fontSize: 14, color: "#2D3748", cursor: "pointer" }}>Cancel</button>
          <button onClick={handleExport} disabled={selected.size === 0} style={{ padding: "10px 24px", borderRadius: 6, border: "none", background: selected.size === 0 ? "#93c5fd" : "#2563eb", color: "#fff", fontSize: 14, fontWeight: 500, cursor: selected.size === 0 ? "not-allowed" : "pointer" }}>Export</button>
        </div>
      </div>
    </div>
  );
}

function generateActivity(listing) {
  const agents = ["Aldo Cardoso", "Nadir Salih", "Jason Mckinney", "Tung Nguyen", "Sriram Senthilkumar"];
  const now = new Date();
  const ago = h => {
    const d = new Date(now.getTime() - h * 3600000);
    return formatInTz(d, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return [
    {
      id: 1, type: "create", actor: listing.assignedTo || agents[0], time: ago(9),
      text: `Created listing, set Status as ${listing.status}, set Type as ${listing.type}, set Category as ${listing.category}, set Vendor as ${listing.vendor || "N/A"}.`,
      isSystem: false,
    },
    {
      id: 2, type: "system", actor: "System", time: ago(9),
      text: null,
      actions: [`Set Group as First Responders Group`, `Applied Default SLA Policy`],
      isSystem: true,
      workflow: "Auto Assignment",
    },
    {
      id: 3, type: "field_change", actor: agents[1], time: ago(7),
      text: `Set Department as ${listing.dept || "IT"}, set Agent as ${listing.assignedTo || agents[0]} and set Group as Network Support Team`,
      isSystem: false,
    },
    {
      id: 4, type: "note", actor: agents[2], time: ago(5),
      text: `Added a public note — Vendor confirmation received. Cost verified as ${listing.cost || "N/A"}. Proceeding with onboarding.`,
      isSystem: false,
    },
    {
      id: 5, type: "system", actor: "System", time: ago(4),
      text: null,
      actions: [`Set Status as ${listing.status}`, `Workflow Ends`],
      isSystem: true,
      workflow: "Status Update Workflow",
    },
    {
      id: 6, type: "field_change", actor: agents[3], time: ago(2),
      text: `Set Expiry as ${listing.expiry || "N/A"} and updated Cost to ${listing.cost || "N/A"}`,
      isSystem: false,
    },
  ];
}

function ActivityTimeline({ listing, activities, onAddNote }) {
  const [noteText, setNoteText] = useState("");
  const [noteMode, setNoteMode] = useState("note");

  const handlePost = () => {
    if (!noteText.trim()) return;
    onAddNote({ text: noteText, mode: noteMode });
    setNoteText("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {activities.map((act, i) => {
        const isLast = i === activities.length - 1;
        const ac = act.isSystem ? { bg: "#e0f2fe", color: "#0369a1" } : (act.type === "note" ? { bg: "#fef3c7", color: "#92400e" } : { bg: T.g200, color: T.active });

        return (
          <div key={act.id} style={{ display: "flex", gap: 10, position: "relative", paddingBottom: isLast ? 0 : 16 }}>
            {!isLast && <div style={{ position: "absolute", left: 14, top: 30, bottom: 0, width: 1.5, background: T.border, zIndex: 0 }} />}
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: ac.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: ac.color, flexShrink: 0, zIndex: 1, border: `1.5px solid ${act.isSystem ? "#bae6fd" : T.border}` }}>
              {act.isSystem ? <Icon name="settings" size={13} color="#0369a1" /> : act.type === "note" ? "NB" : act.actor.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                {!act.isSystem && <span style={{ fontSize: 12, fontWeight: 700, color: T.active }}>{act.actor}</span>}
                <span style={{ fontSize: 11, color: T.muted }}>{act.time}</span>
              </div>
              {act.isSystem ? (
                <div style={{ background: T.g50, border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: T.muted, marginBottom: 6 }}>Executed <span style={{ color: T.active, fontWeight: 700 }}>{act.workflow}</span> workflow from Listing updated event</div>
                  {act.actions.map((a, idx) => <div key={idx} style={{ fontSize: 11, color: "#1A202C", paddingLeft: 12, marginBottom: 2 }}>• {a}</div>)}
                </div>
              ) : (
                <div style={{ background: act.type === "note" ? "#fffbeb" : "#fff", border: `1px solid ${act.type === "note" ? "#fde68a" : T.border}`, borderRadius: 8, padding: "10px 12px" }}>
                  {act.type === "note" && <span style={{ fontSize: 9, background: "#fef3c7", color: "#92400e", padding: "1px 6px", borderRadius: 4, fontWeight: 700, marginBottom: 6, display: "inline-block", textTransform: "uppercase" }}>NOTE</span>}
                  <div style={{ fontSize: 12, color: "#1A202C", lineHeight: 1.6 }}>{act.text}</div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      <div style={{ marginTop: 20, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: `1px solid ${T.border}`, background: T.g50 }}>
          {[{ mode: "note", label: "Add Note" }, { mode: "comment", label: "Comment" }].map(({ mode, label }) => (
            <button key={mode} onClick={() => setNoteMode(mode)} style={{ padding: "8px 14px", border: "none", borderRight: `1px solid ${T.border}`, background: noteMode === mode ? T.active : "transparent", color: noteMode === mode ? "#fff" : T.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>{label}</button>
          ))}
        </div>
        <textarea value={noteText} onChange={e => setNoteText(e.target.value)} placeholder={noteMode === "note" ? "Add a private note..." : "Add a comment..."} style={{ width: "100%", minHeight: 90, padding: 12, border: "none", outline: "none", fontSize: 12, color: "#1A202C", fontFamily: "inherit", resize: "vertical", background: noteMode === "note" ? "#fffdf0" : "#fff", boxSizing: "border-box" }} />
        <div style={{ padding: "8px 12px", borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "flex-end", background: T.g50 }}>
          <button onClick={handlePost} disabled={!noteText.trim()} style={{ padding: "6px 16px", borderRadius: 7, border: "none", background: noteText.trim() ? T.active : "#a7f3d0", color: "#000", fontSize: 12, fontWeight: 700, cursor: noteText.trim() ? "pointer" : "not-allowed" }}>Post</button>
        </div>
      </div>
    </div>
  );
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months !== 1 ? 's' : ''} ago`;
}

function escapeHtml(text) {
  return String(text ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function buildDefaultReplyHtml(listing) {
  const raw = (listing?.requestor || listing?.requester || 'there').trim();
  const name = escapeHtml(raw.split(/\s+/)[0] || raw || 'there');
  const ticketId = escapeHtml(listing?.id || listing?.ticket_number || '');
  const ticketUrl = buildTicketUrl(listing?.id || listing?.ticket_number);
  return `<p>Hi ${name},</p><p><br></p><p><br></p><p>Ticket: ${ticketId}<br><a href="${ticketUrl}">${ticketUrl}</a></p>`;
}

function ListingDetailPage({ listing, onClose, onEdit, onDelete, onUpdate, allListings = [] }) {
  const navigate = useNavigate();
  const size = useWindowSizeSafe();
  const isMobile = size < 992;
  const [tab, setTab] = useState("Details");
  const [localListing, setLocalListing] = useState(listing);
  const [replyText, setReplyText] = useState("");
  const [replyMode, setReplyMode] = useState("reply");
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyToTags, setReplyToTags] = useState(listing.email ? [listing.email] : []);
  const [toInput,    setToInput]    = useState('');
  const [replyCc,    setReplyCc]    = useState(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []);
  const [replyBcc,   setReplyBcc]   = useState([]);
  const [showCc,     setShowCc]     = useState(!!listing.cc);
  const [showBcc,    setShowBcc]    = useState(false);
  const [ccInput,    setCcInput]    = useState('');
  const [bccInput,   setBccInput]   = useState('');
  const [showQuotedMap, setShowQuotedMap] = useState({});
  const [conversations, setConversations] = useState([]);
  const [activePanel, setActivePanel] = useState("summary");
  const [highlightPanel, setHighlightPanel] = useState(null);
  const editorRef    = useRef(null);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);
  const [editorEmpty, setEditorEmpty] = useState(true);
  const replyTemplateTicketRef = useRef(null);

  const applyDefaultReply = useCallback((force = false) => {
    if (!editorRef.current) return;
    const isEmpty = !editorRef.current.innerText?.trim();
    if (!force && !isEmpty) return;
    editorRef.current.innerHTML = buildDefaultReplyHtml(listing);
    setEditorEmpty(false);
  }, [listing.id, listing.requestor, listing._id]);

  // Pre-fill reply when composer opens (not on initial ticket view)
  useEffect(() => {
    if (!composeOpen || replyMode !== 'reply') return;
    const t = setTimeout(() => {
      if (!editorRef.current) return;
      if (replyTemplateTicketRef.current === listing._id) return;
      replyTemplateTicketRef.current = listing._id;
      editorRef.current.innerHTML = buildDefaultReplyHtml(listing);
      setEditorEmpty(false);
    }, 0);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing._id, composeOpen, replyMode]);

  useEffect(() => {
    setComposeOpen(false);
    setShowNoteTypeMenu(false);
    replyTemplateTicketRef.current = null;
  }, [listing._id]);

  // Note type: private vs public
  const [noteIsPrivate, setNoteIsPrivate] = useState(true);
  const [showNoteTypeMenu, setShowNoteTypeMenu] = useState(false);
  // Notify agents (private note only)
  const [notifyAgents, setNotifyAgents] = useState([]);
  const [notifyAgentInput, setNotifyAgentInput] = useState('');
  const [showNotifyDropdown, setShowNotifyDropdown] = useState(false);

  // Master data for dropdowns
  const [masterData, setMasterData] = useState({});
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  // All users for recipient autocomplete (To / Cc / Bcc)
  const [recipientUsers, setRecipientUsers] = useState([]);
  // Requester detail & schedule meeting modals
  const [showRequesterModal, setShowRequesterModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [meetingForm, setMeetingForm] = useState({ title: '', date: '', time: '10:00', duration: '30', notes: '' });
  // Share menu
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [shareCopied, setShareCopied] = useState('');
  const shareRef = useRef(null);

  // Properties form state
  const [props, setProps] = useState({
    priority:     listing.priority  || "Low",
    status:       listing.status    || "Open",
    source:       listing.source    || "Email",
    type:         listing.type      || "Incident",
    group:        listing.group     || "",
    groupId:      listing._assigned_group_id || null,
    assignedTo:   listing.assignedTo || "",
    assignedToId: listing._assigned_to_id || null,
    dept:         listing.dept      || "",
    orgUnit:      listing.orgUnit    || "",
    headOfDept:   listing.headOfDept || "",
    category:     listing.category  || "",
    subCategory:               listing.subCategory               || "",
    additionalSubCategory:     listing.additionalSubCategory     || "",
    endUser:                   listing.endUser                   || "",
    remarks:                   listing.remarks                   || "",
    requestRating:             listing.requestRating             || "",
    procurementCompletionDate: listing.procurementCompletionDate || "",
    reasonForBreach:           listing.reasonForBreach           || "",
  });
  const [saving, setSaving] = useState(false);
  const [propsErrors, setPropsErrors] = useState({});
  const [saveFeedback, setSaveFeedback] = useState(null);

  // SLA state
  const [slaData, setSlaData] = useState(null);
  const [now, setNow] = useState(Date.now());

  // Tick every second to update countdown and live timer display
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load SLA for this ticket
  useEffect(() => {
    if (listing._id) {
      slaAPI.ticketSla(listing._id).then(setSlaData).catch(() => {});
    }
  }, [listing._id]);

  const formatDueCountdown = (dueAt) => {
    if (!dueAt) return null;
    const diffMs = new Date(dueAt).getTime() - now;
    if (diffMs <= 0) return { label: 'Breached', style: { background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5' } };
    const diffD = Math.floor(diffMs / 86400000);
    const diffH = Math.floor((diffMs % 86400000) / 3600000);
    const diffM = Math.floor((diffMs % 3600000) / 60000);
    const isAtRisk = diffMs < 4 * 3600000;
    const text = diffD > 0 ? `${diffD}d ${diffH}h` : diffH > 0 ? `${diffH}h ${diffM}m` : `${diffM}m`;
    if (isAtRisk) return { label: `⚠ ${text}`, style: { background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a' } };
    return { label: `⏱ ${text}`, style: { background: '#dcfce7', color: '#15803d', border: '1px solid #86efac' } };
  };

  const [activities, setActivities] = useState([]);

  const refreshActivities = useCallback(() => {
    if (listing._id) {
      activitiesAPI.list(listing._id).then(data => {
        setActivities((data || []).map(a => ({
          id: a.id,
          actionType: a.action_type,
          actor: a.actor_name || 'System',
          time: formatInTz(a.created_at, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }),
          text: a.note || '',
          oldValue: a.old_value,
          newValue: a.new_value,
          fieldName: a.field_name,
          isSystem: false,
        })));
      }).catch(() => {});
    }
  }, [listing._id]);

  // Fetch real activity logs from API
  useEffect(() => {
    refreshActivities();
  }, [refreshActivities]);

  // Only reset form when a DIFFERENT ticket is opened (id change), not on every parent re-render
  useEffect(() => {
    setLocalListing(listing);
    setProps({
      priority:     listing.priority  || "Low",
      status:       listing.status    || "Open",
      source:       listing.source    || "Email",
      type:         listing.type      || "Incident",
      group:        listing.group     || "",
      groupId:      listing._assigned_group_id || null,
      assignedTo:   listing.assignedTo || "",
      assignedToId: listing._assigned_to_id || null,
      dept:         listing.dept      || "",
      orgUnit:      listing.orgUnit    || "",
      headOfDept:   listing.headOfDept || "",
      category:     listing.category  || "",
      subCategory:               listing.subCategory               || "",
      additionalSubCategory:     listing.additionalSubCategory     || "",
      endUser:                   listing.endUser                   || "",
      remarks:                   listing.remarks                   || "",
      requestRating:             listing.requestRating             || "",
      procurementCompletionDate: listing.procurementCompletionDate || "",
      reasonForBreach:           listing.reasonForBreach           || "",
    });
    setReplyToTags(listing.email ? [listing.email] : []);
    setToInput('');
    setReplyCc(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []);
    setReplyBcc([]);
    setShowCc(!!listing.cc);
    setShowBcc(false);
    setCcInput('');
    setBccInput('');
    setShowQuotedMap({});
    setPropsErrors({});
    setSaveFeedback(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing._id]);

  useEffect(() => {
    masterAPI.all().then(setMasterData).catch(() => {});
    usersAPI.list().then(data => {
      const all = data || [];
      setAgents(all.filter(u => u.role !== 'requester'));
      setRecipientUsers(all.filter(u => u.email));
    }).catch(() => {});
    groupsAPI.list().then(data => setGroups(data || [])).catch(() => {});
  }, []);

  // Load real conversations from API
  useEffect(() => {
    if (listing._id) {
      conversationsAPI.list(listing._id).then(data => {
        setConversations((data || []).map(c => ({
          id: c.id,
          mode: c.mode,
          actor: c.sender_name || c.sender_email || "Agent",
          senderEmail: c.sender_email || '',
          isFromEmail: !!c.is_from_email,
          createdAt: c.created_at,
          time: formatInTz(c.created_at, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }),
          body: c.body,
          text: c.body,
          isHtml: c.body && /<[a-z][\s\S]*>/i.test(c.body),
          attachments: Array.isArray(c.attachments) ? c.attachments : (c.attachments ? JSON.parse(c.attachments) : []),
        })));
      }).catch(() => {});
    }
  }, [listing._id]);

  const handleIconClick = (panelKey) => {
    setActivePanel(panelKey);
    setHighlightPanel(panelKey);
    const el = document.getElementById(`panel-${panelKey}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setTimeout(() => setHighlightPanel(null), 2000);
  };

  const handlePostReply = async () => {
    const html = editorRef.current ? editorRef.current.innerHTML.trim() : '';
    const plain = editorRef.current ? editorRef.current.innerText.trim() : '';
    if (!plain) return;
    const now = formatInTz(new Date(), { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const newMsg = {
      id: Date.now(), mode: replyMode, actor: "Me", time: now,
      body: html, isHtml: true,
      attachments: attachments.map(f => ({ originalname: f.name, size: f.size })),
    };
    setConversations(prev => [...prev, newMsg]);
    if (listing._id) {
      try {
        const toEmails = [...replyToTags, ...(toInput.trim() ? [toInput.trim()] : [])];
        await conversationsAPI.create(listing._id, {
          mode: replyMode,
          body: html,
          to_email: toEmails.join(', '),
          cc: replyCc,
          bcc: replyBcc,
          is_private: replyMode === 'note' ? noteIsPrivate : false,
          notify_agent_ids: replyMode === 'note' && noteIsPrivate ? notifyAgents.map(a => a.id) : [],
        }, attachments);
        refreshActivities();
      } catch (e) {
        console.error('[Reply] send failed:', e.message);
      }
    }
    if (editorRef.current) {
      if (replyMode === 'reply') {
        applyDefaultReply(true);
      } else {
        editorRef.current.innerHTML = '';
        setEditorEmpty(true);
      }
    }
    setAttachments([]);
    setNotifyAgents([]);
    setNotifyAgentInput('');
  };

  const handlePropsUpdate = async () => {
    const errs = {};
    if (!props.dept?.trim()) errs.dept = true;
    if (!props.category?.trim()) errs.category = true;
    if (!props.priority?.trim()) errs.priority = true;
    if (!props.status?.trim()) errs.status = true;
    if (Object.keys(errs).length > 0) {
      setPropsErrors(errs);
      const labels = [];
      if (errs.dept) labels.push('Department');
      if (errs.category) labels.push('Category');
      if (errs.priority) labels.push('Priority');
      if (errs.status) labels.push('Status');
      const msg = `Please fill in required fields: ${labels.join(', ')}.`;
      setSaveFeedback({ type: 'warning', message: msg });
      toast(msg, 'warning');
      return;
    }
    setPropsErrors({});
    setSaveFeedback(null);

    setSaving(true);
    try {
      // Map camelCase props → backend snake_case field names
      const payload = {
        status:         props.status,
        priority:       props.priority,
        type:           props.type,
        source:         props.source   || undefined,
        department:     props.dept     || undefined,
        org_unit:       props.orgUnit    || undefined,
        head_of_department: props.headOfDept || undefined,
        category:       props.category || undefined,
        assigned_group: props.groupId    || undefined,
        assigned_to:    props.assignedToId || undefined,
        sub_category:                props.subCategory            || undefined,
        additional_sub_category:     props.additionalSubCategory  || undefined,
        end_user:                    props.endUser                || undefined,
        remarks:                     props.remarks                || undefined,
        request_rating:              props.requestRating ? parseInt(props.requestRating) : undefined,
        procurement_completion_date: props.procurementCompletionDate || undefined,
        reason_for_breach:           props.reasonForBreach        || undefined,
      };
      // Remove undefined keys so backend ignores them
      Object.keys(payload).forEach(k => payload[k] === undefined && delete payload[k]);

      let savedTicket = null;
      if (listing._id) {
        savedTicket = await ticketsAPI.update(listing._id, payload);

        // Auto-control timer based on new status
        const newStatus = props.status;
        const STOP_STATUSES  = ['Pending User Acceptance', 'Completed', 'Voided', 'Rejected'];
        const PAUSE_STATUSES = ['Pending Action', 'Pending Approval'];
        const currentTimer   = localListing.timer_status || 'stopped';
        if (STOP_STATUSES.includes(newStatus) && currentTimer === 'running') {
          await ticketsAPI.timerAction(listing._id, 'stop').catch(() => {});
        } else if (PAUSE_STATUSES.includes(newStatus) && currentTimer === 'running') {
          await ticketsAPI.timerAction(listing._id, 'pause').catch(() => {});
        } else if (newStatus === 'Open' && currentTimer === 'stopped') {
          await ticketsAPI.timerAction(listing._id, 'start').catch(() => {});
        }

        // Auto-start timer + set status to Open when agent is assigned for the first time
        if (props.assignedToId && !localListing._assigned_to_id) {
          if (currentTimer === 'stopped') {
            await ticketsAPI.timerAction(listing._id, 'start').catch(() => {});
          }
          // If status wasn't already Open, push it to Open
          if (props.status !== 'Open') {
            await ticketsAPI.update(listing._id, { status: 'Open' }).catch(() => {});
          }
        }
      }

      const tsFormat = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' };
      const updated = {
        ...localListing,
        ...props,
        // Keep the IDs in sync so the dropdowns don't reset
        _assigned_group_id: props.groupId,
        _assigned_to_id:    props.assignedToId,
        // sync back the API response if available
        ...(savedTicket ? {
          status:   savedTicket.status,
          priority: savedTicket.priority,
          type:     savedTicket.type,
          // Sync action taker data auto-set by backend
          actionTaker1:          savedTicket.action_taker_1_name    || localListing.actionTaker1    || '',
          actionTaker1Remarks:   savedTicket.action_taker_1_remarks || localListing.actionTaker1Remarks || '',
          actionTaker1Timestamp: savedTicket.action_taker_1_at ? formatInTz(savedTicket.action_taker_1_at, tsFormat) : (localListing.actionTaker1Timestamp || ''),
          actionTaker2:          savedTicket.action_taker_2_name    || localListing.actionTaker2    || '',
          actionTaker2Remarks:   savedTicket.action_taker_2_remarks || localListing.actionTaker2Remarks || '',
          actionTaker2Timestamp: savedTicket.action_taker_2_at ? formatInTz(savedTicket.action_taker_2_at, tsFormat) : (localListing.actionTaker2Timestamp || ''),
          actionTaker3:          savedTicket.action_taker_3_name    || localListing.actionTaker3    || '',
          actionTaker3Remarks:   savedTicket.action_taker_3_remarks || localListing.actionTaker3Remarks || '',
          actionTaker3Timestamp: savedTicket.action_taker_3_at ? formatInTz(savedTicket.action_taker_3_at, tsFormat) : (localListing.actionTaker3Timestamp || ''),
          firstResponseTime: savedTicket.first_response_at ? formatInTz(savedTicket.first_response_at, tsFormat) : (localListing.firstResponseTime || ''),
        } : {}),
      };
      setLocalListing(updated);
      onUpdate && onUpdate(updated);
      refreshActivities();
      const successMsg = 'Ticket properties updated successfully.';
      setSaveFeedback({ type: 'success', message: successMsg });
      toast(successMsg, 'success');
      setTimeout(() => setSaveFeedback(null), 5000);
    } catch (err) {
      console.error("Failed to save properties:", err);
      const errMsg = err.message || 'Failed to save properties. Please try again.';
      setSaveFeedback({ type: 'error', message: errMsg });
      toast(errMsg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const TABS = ["Details", "Activity"];
  const currentIdx = allListings.findIndex(l => l.id === localListing.id);
  const prevItem = currentIdx > 0 ? allListings[currentIdx - 1] : null;
  const nextItem = currentIdx < allListings.length - 1 ? allListings[currentIdx + 1] : null;

  const getStatusStyle = (s) => {
    const map = {
      Open: { bg: "#eff6ff", color: "#1d4ed8", dot: "#3b82f6" },
      "In Progress": { bg: "#fffbeb", color: "#b45309", dot: "#f59e0b" },
      Pending: { bg: "#f5f3ff", color: "#7c3aed", dot: "#8b5cf6" },
      Resolved: { bg: "#EBF0FF", color: "#2C41A8", dot: "#3B5BDB" },
      Closed: { bg: "#FFFFFF", color: "#718096", dot: "#94a3b8" },
    };
    return map[s] || { bg: "#f1f5f9", color: "#718096", dot: "#94a3b8" };
  };
  const sc = getStatusStyle(localListing.status);

  const getPriorityColor = (p) => {
    const map = { Low: "#3B5BDB", Medium: "#f59e0b", High: "#ef4444", Urgent: "#dc2626" };
    return map[p] || "#718096";
  };

  const PANEL_ICONS = [
    { key: "summary",   icon: "info",     title: "Summary" },
    { key: "requester", icon: "user",     title: "Requester" },
    { key: "collab",    icon: "layers",   title: "Collaborate" },
    { key: "props",     icon: "settings", title: "Properties" },
    { key: "time",      icon: "calendar", title: "Time Entries" },
  ];

  const cardStyle = (key) => ({
    background: "#FFFFFF",
    border: highlightPanel === key ? `2px solid ${T.active}` : `1px solid ${T.border}`,
    borderRadius: 10,
    marginBottom: 10,
    overflow: "hidden",
    transition: "border 0.2s, box-shadow 0.2s",
    boxShadow: highlightPanel === key
      ? `0 0 0 4px ${T.g200}, 0 4px 16px rgba(22,163,74,0.18)`
      : "none",
  });

  const selectStyle = {
    width: "100%", padding: "7px 10px", borderRadius: 7, border: `1px solid ${T.border}`,
    fontSize: 13, color: "#1A202C", background: "#FFFFFF", outline: "none",
    fontFamily: "inherit", cursor: "pointer",
  };
  const propFieldStyle = (key) => ({
    ...selectStyle,
    ...(propsErrors[key] ? { borderColor: '#C53030', boxShadow: '0 0 0 2px #fee2e2' } : {}),
  });
  const propLabel = (text, key, required = false) => (
    <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>
      {text}{required && <span style={{ color: "#ef4444" }}> *</span>}
      {propsErrors[key] && <span style={{ color: "#ef4444", fontSize: 10, marginLeft: 6 }}>Required</span>}
    </label>
  );
  const setProp = (patch) => {
    setProps(p => ({ ...p, ...patch }));
    const cleared = {};
    Object.keys(patch).forEach(k => {
      if (propsErrors[k]) cleared[k] = false;
    });
    if (Object.keys(cleared).length) setPropsErrors(e => ({ ...e, ...cleared }));
  };

  // Close share menu on outside click
  useEffect(() => {
    if (!showShareMenu) return;
    const handler = e => { if (shareRef.current && !shareRef.current.contains(e.target)) setShowShareMenu(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showShareMenu]);

  const mdOptions = (type) => (masterData[type] || []).map(d => d.value);

  return (
    <div style={{ position: "absolute", inset: 0, background: "#FFFFFF", zIndex: 250, display: "flex", flexDirection: "column", overflow: "hidden" }}>

      {/* Top bar */}
      <div style={{ background: "#FFFFFF", borderBottom: `1px solid ${T.border}`, padding: "0 16px", height: 44, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
        <span style={{ fontSize: 12, color: T.active, cursor: "pointer", fontWeight: 600 }} onClick={onClose}>Service Request</span>
        <Icon name="chevronR" size={12} color={T.muted} />
        <span style={{ fontSize: 12, color: "#2D3748", fontWeight: 700 }}>{localListing.id}</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {/* Share button with dropdown */}
          <div ref={shareRef} style={{ position: 'relative' }}>
            <button onClick={() => setShowShareMenu(v => !v)}
              style={{ height: 28, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: showShareMenu ? T.g50 : "#FFFFFF", fontSize: 12, color: "#2D3748", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
              <Icon name="copy" size={12} color={T.muted} /> Share
            </button>
            {showShareMenu && (
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 400, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.13)', width: 220, overflow: 'hidden' }}>
                {[
                  {
                    label: shareCopied === 'link' ? '✓ Link Copied!' : 'Copy Ticket Link',
                    icon: 'copy',
                    action: () => {
                      const url = buildTicketUrl(localListing.id);
                      navigator.clipboard.writeText(url).then(() => {
                        setShareCopied('link');
                        setTimeout(() => { setShareCopied(''); setShowShareMenu(false); }, 1500);
                      });
                    },
                  },
                  {
                    label: shareCopied === 'id' ? '✓ ID Copied!' : 'Copy Ticket ID',
                    icon: 'copy',
                    action: () => {
                      navigator.clipboard.writeText(localListing.id).then(() => {
                        setShareCopied('id');
                        setTimeout(() => { setShareCopied(''); setShowShareMenu(false); }, 1500);
                      });
                    },
                  },
                ].map(({ label, icon, action }) => (
                  <button key={label} onClick={action} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: label.startsWith('✓') ? '#16a34a' : '#2D3748', fontWeight: label.startsWith('✓') ? 700 : 500, textAlign: 'left' }}
                    onMouseEnter={e => { e.currentTarget.style.background = T.g50; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}>
                    <Icon name={icon} size={13} color={label.startsWith('✓') ? '#16a34a' : T.muted} />
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Edit button */}
          <button onClick={() => onEdit(localListing)}
            style={{ height: 28, padding: "0 10px", borderRadius: 6, border: `1px solid ${T.border}`, background: "#FFFFFF", fontSize: 12, color: "#2D3748", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
            <Icon name="edit" size={12} color={T.muted} /> Edit
          </button>
          <button onClick={() => { onDelete(localListing.id); onClose(); }}
            style={{ height: 28, padding: "0 10px", borderRadius: 6, border: "1px solid #fca5a5", background: "#fff5f5", fontSize: 12, color: "#dc2626", cursor: "pointer", display: "flex", alignItems: "center", gap: 4, fontWeight: 500 }}>
            <Icon name="trash" size={12} color="#dc2626" /> Delete
          </button>
          <div style={{ width: 1, height: 18, background: T.border, margin: "0 2px" }} />
          {[{ dir: "L", item: prevItem }, { dir: "R", item: nextItem }].map(({ dir, item }) => (
            <button key={dir} disabled={!item} onClick={() => item && setLocalListing(item)}
              style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${T.border}`, background: item ? "#fff" : T.g50, cursor: item ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name={`chevron${dir}`} size={12} color={item ? "#2D3748" : T.border2} />
            </button>
          ))}
          <button onClick={onClose}
            style={{ width: 26, height: 26, borderRadius: 6, border: `1px solid ${T.border}`, background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Icon name="close" size={13} color={T.muted} />
          </button>
        </div>
      </div>

      {/* Ticket header — Freshdesk style */}
      <div style={{ background: "#FFFFFF", borderBottom: `1px solid ${T.border}`, padding: "12px 16px", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          {/* Colored ticket icon */}
          <div style={{ width: 40, height: 40, borderRadius: 10, background: sc.dot, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
            <Icon name="tag" size={18} color="#fff" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Title + status badge */}
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#1A202C", lineHeight: 1.4, flex: 1 }}>
                {localListing.id ? `${localListing.id} — ` : ''}{localListing.name}
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 99, border: `1px solid ${sc.dot}33`, whiteSpace: "nowrap", flexShrink: 0, marginTop: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: sc.dot, display: "inline-block" }} />
                {localListing.status}
              </span>
            </div>
            {/* Reporter line */}
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4, fontSize: 12, color: "#718096", marginBottom: 3 }}>
              <span style={{ fontWeight: 600, color: "#1A202C" }}>{localListing.requestor || localListing.email || "Unknown"}</span>
              <span>reported</span>
              {localListing._created_at
                ? <span>
                    <span style={{ color: "#1A202C", fontWeight: 500 }}>{timeAgo(localListing._created_at)}</span>
                    {' '}(<span style={{ color: "#4A5568" }}>{formatInTz(localListing._created_at, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}</span>)
                  </span>
                : localListing.created && <span style={{ color: "#4A5568" }}>({localListing.created})</span>
              }
              <span>via</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontWeight: 600, color: "#1A202C" }}>
                {localListing.source || "Email"}
              </span>
              <Icon name={localListing.source === "Phone" ? "phone" : "mail"} size={13} color="#718096" />
            </div>
            {/* To row */}
            {localListing.email && (
              <div style={{ fontSize: 12, marginBottom: 2 }}>
                <span style={{ fontWeight: 700, color: "#4A5568", marginRight: 4 }}>To:</span>
                <span style={{ color: T.active }}>{localListing.email}</span>
              </div>
            )}
            {/* Cc row */}
            {localListing.cc && (
              <div style={{ fontSize: 12 }}>
                <span style={{ fontWeight: 700, color: "#4A5568", marginRight: 4 }}>Cc:</span>
                <span style={{ color: T.active }}>{localListing.cc}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ background: "#FFFFFF", borderBottom: `1px solid ${T.border}`, padding: "0 16px", display: "flex", gap: 0, flexShrink: 0, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ padding: "9px 13px", border: "none", background: "transparent", fontSize: 12, fontWeight: tab === t ? 700 : 400, color: tab === t ? T.active : "#718096", cursor: "pointer", borderBottom: tab === t ? `2px solid ${T.active}` : "2px solid transparent", marginBottom: -1, whiteSpace: "nowrap" }}>
            {t}
          </button>
        ))}
      </div>

      {/* Body */}
      <div style={{ flex: 1, display: "flex", overflow: "hidden" }}>

        {/* Main content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px", background: "#FFFFFF" }}>
          {tab === "Details" && (
            <div style={{ maxWidth: 740 }}>
              {/* Timer */}
              <TicketTimer
                ticketId={localListing._id}
                initialTimerStatus={localListing.timer_status}
                initialElapsedMs={localListing.timer_elapsed_ms}
                initialStartedAt={localListing.timer_started_at}
                ticketStatus={localListing.status}
                userRole={(JSON.parse(localStorage.getItem('user') || '{}')).role}
                onTimerChange={result => {
                  const timerPatch = {
                    timer_status: result.timer_status,
                    timer_elapsed_ms: Number(result.timer_elapsed_ms) || 0,
                    timer_started_at: result.timer_started_at,
                  };
                  setLocalListing(prev => ({ ...prev, ...timerPatch }));
                  onUpdate && onUpdate({ ...localListing, ...timerPatch });
                }}
              />

              {/* Description card */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 16, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: 13, color: "#1A202C" }}>Description</div>
                <div style={{ padding: "14px 16px" }}>
                  {(() => {
                    let desc = localListing.description || localListing.name || '';
                    // For email-sourced tickets: if description is blank or only the subject,
                    // try to use the first inbound conversation body instead (richer content).
                    if (localListing.source === 'Email') {
                      const subjectText = (localListing.subject || '').trim().toLowerCase();
                      const descText = desc.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                      if (!descText || descText === subjectText) {
                        const firstEmailConv = conversations.find(c => c.is_from_email && c.body);
                        if (firstEmailConv) {
                          const convText = (firstEmailConv.body || '').replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
                          if (convText && convText !== subjectText) {
                            desc = firstEmailConv.body;
                          }
                        }
                      }
                    }
                    const descText = desc.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
                    const subjectText = (localListing.subject || '').trim();
                    const isMissingBody = !descText || descText.toLowerCase() === subjectText.toLowerCase();
                    if (isMissingBody && localListing.source === 'Email') {
                      return (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 12, color: '#92400e' }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>⚠️</span>
                          <div>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>Email body could not be extracted</div>
                            <div style={{ fontWeight: 400 }}>This ticket was created from an email, but the email body was minimal or encoded in a format the parser could not read (e.g. Outlook TNEF/winmail.dat). The full content may be visible when the sender replies or resends the email.</div>
                          </div>
                        </div>
                      );
                    }
                    const isHtml = /<[a-z][\s\S]*>/i.test(desc);
                    return isHtml
                      ? <div className="email-body" style={{ fontSize: 13, color: "#2D3748", lineHeight: 1.7, wordBreak: "break-word" }} dangerouslySetInnerHTML={{ __html: desc }} />
                      : <div style={{ fontSize: 13, color: "#2D3748", lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{desc}</div>;
                  })()}
                  {/* Attachments from the initial inbound email */}
                  {(() => {
                    const firstFromEmail = conversations.find(c => c.attachments && c.attachments.length > 0);
                    if (!firstFromEmail) return null;
                    return (
                      <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.border}` }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                          Attachments ({firstFromEmail.attachments.length})
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                          {firstFromEmail.attachments.map((a, i) => (
                            <a key={i} href={a.url ? `${SERVER_URL}${a.url}` : '#'} target="_blank" rel="noreferrer"
                              style={{ display: "flex", alignItems: "center", gap: 6, background: "#F8FAFC", border: `1px solid ${T.border}`, color: "#2D3748", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, textDecoration: "none", minWidth: 140 }}>
                              <span style={{ fontSize: 16 }}>📎</span>
                              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.3 }}>
                                <span style={{ color: T.active }}>{a.originalname || a.name}</span>
                                {a.size && <span style={{ fontSize: 10, color: T.muted, fontWeight: 500 }}>{(a.size / 1024).toFixed(1)} KB</span>}
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Conversations */}
              <div style={{ background: "#FFFFFF", border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, fontWeight: 700, fontSize: 13, color: "#1A202C" }}>Conversations</div>
                {conversations.length > 0 && (
                  <div style={{ padding: "10px 16px", display: "flex", flexDirection: "column", gap: 10 }}>
                    {conversations.map(c => {
                      // Freshdesk-style header line: "{actor} {verb} {ago} ({date}) to {to}, Cc: {cc}"
                      const verb =
                        c.mode === 'note'    ? 'added a note' :
                        c.mode === 'forward' ? 'forwarded' :
                        c.isFromEmail        ? 'replied' :
                                               'replied';
                      const created = c.createdAt ? new Date(c.createdAt) : null;
                      const agoStr  = created ? timeAgo(c.createdAt) : c.time;
                      const fullDate = created ? formatInTz(created, { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true }) : '';
                      // Determine recipients
                      const toEmail = c.isFromEmail
                        ? (process.env.REACT_APP_SUPPORT_EMAIL || 'support')
                        : (localListing.email || localListing.requester_email || '');
                      const ccEmail = localListing.cc || '';
                      const channelIcon = c.mode === 'note' ? '✎' : c.mode === 'forward' ? '→' : c.isFromEmail ? '✉' : '↩';
                      return (
                      <div key={c.id} style={{ display: "flex", gap: 10 }}>
                        <Avatar name={c.actor || c.sender_name || "?"} size={32} />
                        <div style={{ flex: 1, background: c.mode === "note" ? "#fffdf0" : "#EFF6FF", border: `1px solid ${c.mode === "note" ? "#fde68a" : "#DBE7FB"}`, borderRadius: 8, padding: "12px 14px", position: 'relative' }}>
                          {/* Action buttons top-right */}
                          <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 4 }}>
                            <button title="Reply" onClick={() => {
                              setReplyToTags(listing.email ? [listing.email] : []);
                              setReplyCc(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []);
                              setShowCc(!!listing.cc); setShowBcc(false);
                              setReplyMode('reply');
                              setComposeOpen(true);
                              document.getElementById('reply-editor-anchor')?.scrollIntoView({ behavior: 'smooth' });
                              setTimeout(() => applyDefaultReply(true), 0);
                            }}
                              style={{ width: 26, height: 26, border: `1px solid ${T.border}`, background: '#fff', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: 13 }}>↪</button>
                            <button title="Forward" onClick={() => {
                              const allTo = [...new Set([
                                ...(listing.email ? [listing.email] : []),
                                ...(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []),
                              ])];
                              setReplyToTags(allTo);
                              setReplyCc([]); setReplyBcc([]);
                              setShowCc(false); setShowBcc(false);
                              setReplyMode('forward');
                              setComposeOpen(true);
                              if (editorRef.current) { editorRef.current.innerHTML = ''; setEditorEmpty(true); }
                              document.getElementById('reply-editor-anchor')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                              style={{ width: 26, height: 26, border: `1px solid ${T.border}`, background: '#fff', borderRadius: 5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: T.muted, fontSize: 13 }}>⤷</button>
                          </div>
                          {/* Header line */}
                          <div style={{ fontSize: 12, color: '#475569', lineHeight: 1.5, paddingRight: 70, marginBottom: 10 }}>
                            <span style={{ color: T.active, fontWeight: 700 }}>{c.actor}</span>
                            <span> {verb} {agoStr}{fullDate ? ` (${fullDate})` : ''}</span>
                            {toEmail && (<><span> to </span><span style={{ color: T.active }}>{toEmail}</span></>)}
                            {ccEmail && (<><span>,&nbsp;Cc: </span><span style={{ color: T.active }}>{ccEmail}</span></>)}
                          </div>
                          {/* Render body with quoted-text collapse */}
                          {(() => {
                            const body = c.body || c.text;
                            if (!body) return null;
                            const isHtml = c.isHtml || /<[a-z][\s\S]*>/i.test(body);
                            const showQ = !!showQuotedMap[c.id];
                            const toggleQ = () => setShowQuotedMap(m => ({ ...m, [c.id]: !showQ }));
                            const qBtn = (
                              <button onClick={toggleQ} style={{ border: `1px solid ${T.border}`, background: '#fff', color: T.muted, borderRadius: 12, padding: '2px 12px', fontSize: 13, cursor: 'pointer', marginTop: 10, display: 'inline-flex', alignItems: 'center', lineHeight: 1, height: 22 }}>
                                {showQ ? '▲' : '···'}
                              </button>
                            );
                            if (isHtml) {
                              if (/<blockquote/i.test(body)) {
                                const main = body.replace(/<blockquote[\s\S]*?<\/blockquote>/gi, '').replace(/(<br\s*\/?>[\s\n]*)+$/, '');
                                return (
                                  <div>
                                    <div style={{ fontSize: 13, color: '#2D3748', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: main }} />
                                    {qBtn}
                                    {showQ && <div style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 10, marginTop: 6, opacity: 0.65 }} dangerouslySetInnerHTML={{ __html: body }} />}
                                  </div>
                                );
                              }
                              return <div style={{ fontSize: 13, color: '#2D3748', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: body }} />;
                            }
                            // Plain text — detect quoted separator
                            const sep = body.match(/(-{5,}|_{5,})\s*(From:|Sent:)/i);
                            if (sep) {
                              const idx = body.indexOf(sep[0]);
                              return (
                                <div>
                                  <div style={{ fontSize: 13, color: '#2D3748', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{body.substring(0, idx).trim()}</div>
                                  {qBtn}
                                  {showQ && <div style={{ borderLeft: '3px solid #e2e8f0', paddingLeft: 10, marginTop: 6, opacity: 0.65, fontSize: 11, color: '#4A5568', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{body.substring(idx)}</div>}
                                </div>
                              );
                            }
                            return <div style={{ fontSize: 13, color: '#2D3748', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{body}</div>;
                          })()}
                          {/* Attachment list */}
                          {((c.attachments && c.attachments.length > 0) || (c.attachmentNames && c.attachmentNames.length > 0)) && (
                            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
                              {(c.attachments || c.attachmentNames.map(n => ({ originalname: n }))).map((a, i) => (
                                <a key={i} href={a.url ? `${SERVER_URL}${a.url}` : '#'} target="_blank" rel="noreferrer"
                                  style={{ display: "flex", alignItems: "center", gap: 4, background: "#EBF0FF", color: "#2C41A8", borderRadius: 5, padding: "3px 8px", fontSize: 11, fontWeight: 600, textDecoration: "none" }}>
                                  📎 {a.originalname || a.name}
                                </a>
                              ))}
                            </div>
                          )}
                          {c.mode === "note" && <span style={{ fontSize: 10, color: "#92400e", fontWeight: 600, background: "#fef3c7", padding: "1px 5px", borderRadius: 4, marginTop: 5, display: "inline-block" }}>Private Note</span>}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
                {/* Rich text reply editor */}
                <div id="reply-editor-anchor" style={{ padding: "10px 16px", borderTop: conversations.length > 0 ? `1px solid ${T.border}` : "none" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                    <Avatar name="Me" size={26} />
                    <div style={{ display: "flex", gap: 2, background: T.g50, borderRadius: 7, padding: 3, border: `1px solid ${T.border}`, flexWrap: "wrap" }}>
                      {/* Reply */}
                      <button onClick={() => {
                        setReplyToTags(listing.email ? [listing.email] : []);
                        setReplyCc(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []);
                        setShowCc(!!listing.cc); setShowBcc(false);
                        setReplyMode("reply");
                        setComposeOpen(true);
                        setShowNoteTypeMenu(false);
                        setTimeout(() => applyDefaultReply(true), 0);
                      }} style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: composeOpen && replyMode === "reply" ? T.active : "transparent", color: composeOpen && replyMode === "reply" ? "#fff" : "#2D3748", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>↩ Reply</button>
                      {/* Forward */}
                      <button onClick={() => {
                        const allTo = [...new Set([
                          ...(listing.email ? [listing.email] : []),
                          ...(listing.cc ? listing.cc.split(',').map(e => e.trim()).filter(Boolean) : []),
                        ])];
                        setReplyToTags(allTo);
                        setReplyCc([]); setReplyBcc([]);
                        setShowCc(false); setShowBcc(false);
                        setReplyMode("forward");
                        setComposeOpen(true);
                        setShowNoteTypeMenu(false);
                        if (editorRef.current) { editorRef.current.innerHTML = ''; setEditorEmpty(true); }
                      }} style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: composeOpen && replyMode === "forward" ? T.active : "transparent", color: composeOpen && replyMode === "forward" ? "#fff" : "#2D3748", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>→ Forward</button>
                      {/* Note split button */}
                      <div style={{ position: "relative" }}>
                        <button onClick={() => setShowNoteTypeMenu(v => !v)} style={{ padding: "4px 10px", borderRadius: 5, border: "none", background: composeOpen && replyMode === "note" ? (noteIsPrivate ? "#f59e0b" : "#3b82f6") : "transparent", color: composeOpen && replyMode === "note" ? "#fff" : "#2D3748", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
                          {composeOpen && replyMode === "note" ? (noteIsPrivate ? "🔒" : "🌐") : "✎"}
                          {composeOpen && replyMode === "note" ? (noteIsPrivate ? "Private note" : "Public note") : "Note"}
                          <span style={{ fontSize: 9 }}>▾</span>
                        </button>
                        {showNoteTypeMenu && (
                          <>
                            <div style={{ position: "fixed", inset: 0, zIndex: 98 }} onClick={() => setShowNoteTypeMenu(false)} />
                            <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 99, background: "#fff", border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,0.14)", minWidth: 210, overflow: "hidden" }}>
                              <div style={{ padding: "7px 12px", fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", borderBottom: `1px solid ${T.border}` }}>Mark note as</div>
                              <button onClick={() => { setNoteIsPrivate(true); setReplyMode("note"); setComposeOpen(true); setShowNoteTypeMenu(false); if (editorRef.current) { editorRef.current.innerHTML = ''; setEditorEmpty(true); } }} style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", padding: "10px 14px", background: (replyMode === "note" && noteIsPrivate) ? "#eff6ff" : "none", border: "none", cursor: "pointer", textAlign: "left" }}>
                                <span style={{ fontSize: 15 }}>🔒</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Add private note</div>
                                  <div style={{ fontSize: 11, color: T.muted }}>Hidden to the customer</div>
                                </div>
                                {replyMode === "note" && noteIsPrivate && <span style={{ color: T.active, fontWeight: 800 }}>✓</span>}
                              </button>
                              <button onClick={() => { setNoteIsPrivate(false); setReplyMode("note"); setComposeOpen(true); setShowNoteTypeMenu(false); if (editorRef.current) { editorRef.current.innerHTML = ''; setEditorEmpty(true); } }} style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%", padding: "10px 14px", background: (replyMode === "note" && !noteIsPrivate) ? "#eff6ff" : "none", border: "none", borderTop: `1px solid ${T.border}`, cursor: "pointer", textAlign: "left" }}>
                                <span style={{ fontSize: 15 }}>🌐</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Add public note</div>
                                  <div style={{ fontSize: 11, color: T.muted }}>Visible to the customer</div>
                                </div>
                                {replyMode === "note" && !noteIsPrivate && <span style={{ color: T.active, fontWeight: 800 }}>✓</span>}
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {composeOpen && (
                  <>
                  {/* Email header: From / To / Cc / Bcc — visible for reply and forward */}
                  {(replyMode === 'reply' || replyMode === 'forward') && (() => {
                    const meUser = JSON.parse(localStorage.getItem('user') || '{}');
                    const fromDisplay = meUser.name ? `"${meUser.name}" <${meUser.email || ''}>` : (meUser.email || 'Support');
                    const rowStyle = { display: 'flex', alignItems: 'center', padding: '5px 10px', borderBottom: `1px solid ${T.border}`, gap: 4 };
                    const labelStyle = { fontSize: 11, fontWeight: 700, color: T.muted, width: 48, flexShrink: 0 };
                    const inputStyle = { flex: 1, border: 'none', background: 'transparent', fontSize: 13, color: '#1A202C', outline: 'none', fontFamily: 'inherit', minWidth: 60 };
                    const pillStyle = { display: 'inline-flex', alignItems: 'center', gap: 3, background: '#EBF0FF', color: '#2C41A8', borderRadius: 99, padding: '2px 8px', fontSize: 11, fontWeight: 600, flexShrink: 0 };
                    const bccPillStyle = { ...pillStyle, background: '#fef3c7', color: '#92400e' };
                    const pillXStyle = { border: 'none', background: 'none', cursor: 'pointer', color: '#718096', fontSize: 12, lineHeight: 1, padding: '0 0 0 2px' };
                    const toggleBtnStyle = { fontSize: 11, color: T.muted, border: `1px solid ${T.border}`, background: '#f8fafc', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', flexShrink: 0 };
                    const closeBtnStyle = { border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 15, lineHeight: 1, padding: '0 2px', flexShrink: 0 };
                    const handleTagKey = (e, input, setInput, setArr) => {
                      if ((e.key === 'Enter' || e.key === ',') && input.trim()) {
                        e.preventDefault();
                        setArr(arr => [...arr, input.trim().replace(/,$/, '')]);
                        setInput('');
                      } else if (e.key === 'Backspace' && !input) {
                        setArr(arr => arr.slice(0, -1));
                      }
                    };
                    // Recipient input with user autocomplete dropdown (portal-based)
                    const makeRecipientInput = (input, setInput, tags, setTags, placeholder) => {
                      const q = input.toLowerCase();
                      const filtered = q ? recipientUsers.filter(u =>
                        !tags.includes(u.email) &&
                        ((u.email || '').toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q))
                      ) : [];
                      return (
                        <RecipientInputField
                          value={input}
                          onChange={val => setInput(val)}
                          onAddTag={email => setTags(arr => [...arr, email])}
                          onRemoveLastTag={() => setTags(arr => arr.slice(0, -1))}
                          placeholder={tags.length === 0 ? placeholder : ''}
                          suggestions={filtered}
                        />
                      );
                    };
                    const lastRowBorder = (!showCc && !showBcc) ? 'none' : `1px solid ${T.border}`;
                    return (
                      <div style={{ border: `1px solid ${T.border}`, borderRadius: 7, marginBottom: 8, background: '#fff', overflow: 'visible', position: 'relative', zIndex: 10 }}>
                        {/* From row */}
                        <div style={rowStyle}>
                          <span style={labelStyle}>From:</span>
                          <span style={{ fontSize: 13, color: '#374151', flex: 1 }}>{fromDisplay}</span>
                        </div>
                        {/* To row — multi-tag input */}
                        <div style={{ ...rowStyle, borderBottom: lastRowBorder, flexWrap: 'wrap' }}>
                          <span style={labelStyle}>To:</span>
                          <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minWidth: 0 }}>
                            {replyToTags.map((em, i) => (
                              <span key={i} style={pillStyle}>
                                {em}
                                <button style={pillXStyle} onClick={() => setReplyToTags(a => a.filter((_, j) => j !== i))}>✕</button>
                              </span>
                            ))}
                            {makeRecipientInput(toInput, setToInput, replyToTags, setReplyToTags, 'Add recipient...')}
                          </div>
                          {/* Show Cc / Bcc toggle buttons in To row when those rows are hidden */}
                          {!showCc  && <button style={toggleBtnStyle} onClick={() => setShowCc(true)}>Cc</button>}
                          {!showBcc && <button style={toggleBtnStyle} onClick={() => setShowBcc(true)}>Bcc</button>}
                        </div>
                        {/* Cc row */}
                        {showCc && (
                          <div style={{ ...rowStyle, borderBottom: showBcc ? `1px solid ${T.border}` : 'none', flexWrap: 'wrap' }}>
                            <span style={labelStyle}>Cc:</span>
                            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minWidth: 0 }}>
                              {replyCc.map((em, i) => (
                                <span key={i} style={pillStyle}>
                                  {em}
                                  <button style={pillXStyle} onClick={() => setReplyCc(a => a.filter((_, j) => j !== i))}>✕</button>
                                </span>
                              ))}
                              {makeRecipientInput(ccInput, setCcInput, replyCc, setReplyCc, 'Add CC...')}
                            </div>
                            <button style={closeBtnStyle} title="Remove Cc" onClick={() => { setShowCc(false); setReplyCc([]); setCcInput(''); }}>×</button>
                          </div>
                        )}
                        {/* Bcc row */}
                        {showBcc && (
                          <div style={{ ...rowStyle, borderBottom: 'none', flexWrap: 'wrap' }}>
                            <span style={labelStyle}>Bcc:</span>
                            <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', minWidth: 0 }}>
                              {replyBcc.map((em, i) => (
                                <span key={i} style={bccPillStyle}>
                                  {em}
                                  <button style={pillXStyle} onClick={() => setReplyBcc(a => a.filter((_, j) => j !== i))}>✕</button>
                                </span>
                              ))}
                              {makeRecipientInput(bccInput, setBccInput, replyBcc, setReplyBcc, 'Add BCC...')}
                            </div>
                            <button style={closeBtnStyle} title="Remove Bcc" onClick={() => { setShowBcc(false); setReplyBcc([]); setBccInput(''); }}>×</button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {/* Notify agents row — private note only */}
                  {replyMode === 'note' && noteIsPrivate && (
                    <div style={{ border: `1px solid #fde68a`, borderRadius: 7, marginBottom: 8, background: '#fffdf0', overflow: 'visible' }}>
                      <div style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', flexWrap: 'wrap', gap: 4 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', flexShrink: 0, marginRight: 4 }}>Notify to:</span>
                        <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', position: 'relative' }}>
                          {notifyAgents.map((a, i) => (
                            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, background: '#EBF0FF', color: '#2C41A8', borderRadius: 99, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                              {a.name}
                              <button style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#718096', fontSize: 12, lineHeight: 1 }} onClick={() => setNotifyAgents(prev => prev.filter((_, j) => j !== i))}>✕</button>
                            </span>
                          ))}
                          <div style={{ position: 'relative', flex: 1, minWidth: 120 }}>
                            <input value={notifyAgentInput}
                              onChange={e => { setNotifyAgentInput(e.target.value); setShowNotifyDropdown(true); }}
                              onFocus={() => setShowNotifyDropdown(true)}
                              onBlur={() => setTimeout(() => setShowNotifyDropdown(false), 180)}
                              placeholder={notifyAgents.length === 0 ? 'Search agent by name...' : ''}
                              style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, color: '#1A202C', outline: 'none', fontFamily: 'inherit' }}
                            />
                            {showNotifyDropdown && (() => {
                              const q = notifyAgentInput.trim().toLowerCase();
                              const filtered = (agents || []).filter(a =>
                                !notifyAgents.some(na => na.id === a.id) &&
                                ((a.name || '').toLowerCase().includes(q) || (a.email || '').toLowerCase().includes(q))
                              ).slice(0, 10);
                              return filtered.length > 0 ? (
                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.1)', zIndex: 10, maxHeight: 180, overflowY: 'auto', marginTop: 2 }}>
                                  {filtered.map(a => (
                                    <div key={a.id} onMouseDown={e => e.preventDefault()}
                                      onClick={() => { setNotifyAgents(prev => [...prev, { id: a.id, name: a.name, email: a.email }]); setNotifyAgentInput(''); setShowNotifyDropdown(false); }}
                                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 12, borderBottom: `1px solid ${T.border}` }}
                                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                      <Avatar name={a.name} size={22} />
                                      <div>
                                        <div style={{ fontWeight: 600, color: '#1A202C' }}>{a.name}</div>
                                        <div style={{ fontSize: 11, color: T.muted }}>{a.email}</div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : null;
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Public note info banner */}
                  {replyMode === 'note' && !noteIsPrivate && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', marginBottom: 8, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 7, fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                      🌐 Public note — <span style={{ fontWeight: 400, color: '#374151' }}>this note will be visible to the customer</span>
                    </div>
                  )}
                  {/* Editor container */}
                  <div style={{ border: `1.5px solid ${replyMode === "note" ? (noteIsPrivate ? "#fde68a" : "#bfdbfe") : T.border}`, borderRadius: 8, overflow: "hidden", background: replyMode === "note" ? (noteIsPrivate ? "#fffdf0" : "#eff6ff") : "#fff" }}>
                    {/* Formatting toolbar */}
                    <div style={{ display: "flex", alignItems: "center", gap: 2, padding: "5px 8px", borderBottom: `1px solid ${T.border}`, background: "#f8fafc", flexWrap: "wrap" }}>
                      {[
                        { cmd: "bold",          label: "B",  title: "Bold",          style: { fontWeight: 800 } },
                        { cmd: "italic",        label: "I",  title: "Italic",        style: { fontStyle: "italic" } },
                        { cmd: "underline",     label: "U",  title: "Underline",     style: { textDecoration: "underline" } },
                        { cmd: "strikeThrough", label: "S",  title: "Strikethrough", style: { textDecoration: "line-through" } },
                      ].map(({ cmd, label, title, style }) => (
                        <button key={cmd} title={title}
                          onMouseDown={e => { e.preventDefault(); document.execCommand(cmd, false, null); }}
                          style={{ width: 26, height: 26, borderRadius: 4, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", fontSize: 12, color: "#2D3748", display: "flex", alignItems: "center", justifyContent: "center", ...style }}>
                          {label}
                        </button>
                      ))}
                      <div style={{ width: 1, height: 16, background: T.border, margin: "0 3px" }} />
                      {[
                        { cmd: "insertUnorderedList", label: "• List",  title: "Bullet list" },
                        { cmd: "insertOrderedList",   label: "1. List", title: "Numbered list" },
                      ].map(({ cmd, label, title }) => (
                        <button key={cmd} title={title}
                          onMouseDown={e => { e.preventDefault(); document.execCommand(cmd, false, null); }}
                          style={{ height: 26, padding: "0 7px", borderRadius: 4, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", fontSize: 11, color: "#2D3748" }}>
                          {label}
                        </button>
                      ))}
                      <div style={{ width: 1, height: 16, background: T.border, margin: "0 3px" }} />
                      <button title="Insert link"
                        onMouseDown={e => { e.preventDefault(); const url = prompt("Enter URL:"); if (url) document.execCommand("createLink", false, url.startsWith("http") ? url : "https://" + url); }}
                        style={{ width: 26, height: 26, borderRadius: 4, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        🔗
                      </button>
                      <button title="Attach files" onMouseDown={e => { e.preventDefault(); fileInputRef.current?.click(); }}
                        style={{ width: 26, height: 26, borderRadius: 4, border: `1px solid ${T.border}`, background: "#fff", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        📎
                      </button>
                      <input ref={fileInputRef} type="file" multiple style={{ display: "none" }}
                        onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])} />
                    </div>
                    {/* Contenteditable area with placeholder */}
                    <div style={{ position: "relative" }}>
                      {editorEmpty && (
                        <div style={{ position: "absolute", top: 9, left: 12, fontSize: 13, color: "#9ca3af", pointerEvents: "none" }}>
                          {replyMode === "note" ? (noteIsPrivate ? "Add a private note (hidden from customer)..." : "Add a public note (visible to customer)...") : replyMode === "forward" ? "Enter recipient and message..." : "Type your reply..."}
                        </div>
                      )}
                      <div ref={editorRef} contentEditable suppressContentEditableWarning
                        onInput={() => setEditorEmpty(!editorRef.current?.innerText?.trim())}
                        onFocus={() => setEditorEmpty(!editorRef.current?.innerText?.trim())}
                        style={{ minHeight: 100, padding: "9px 12px", fontSize: 13, color: "#2D3748", lineHeight: 1.6, outline: "none", fontFamily: "inherit", position: "relative", zIndex: 1 }} />
                    </div>
                    {/* Attachment previews */}
                    {attachments.length > 0 && (
                      <div style={{ padding: "6px 10px", borderTop: `1px solid ${T.border}`, display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {attachments.map((f, i) => (
                          <span key={i} style={{ display: "flex", alignItems: "center", gap: 4, background: "#EBF0FF", color: "#2C41A8", borderRadius: 5, padding: "3px 8px", fontSize: 11, fontWeight: 600 }}>
                            📎 {f.name} <span style={{ color: "#718096" }}>({(f.size / 1024).toFixed(0)}KB)</span>
                            <button onClick={() => setAttachments(a => a.filter((_, j) => j !== i))}
                              style={{ border: "none", background: "none", cursor: "pointer", color: "#718096", fontSize: 12, lineHeight: 1, padding: "0 0 0 2px" }}>✕</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
                    <button onClick={handlePostReply} disabled={editorEmpty && attachments.length === 0}
                      style={{ padding: "6px 18px", borderRadius: 7, border: "none", background: (!editorEmpty || attachments.length > 0) ? T.active : "#a7f3d0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: (!editorEmpty || attachments.length > 0) ? "pointer" : "not-allowed" }}>
                      Send
                    </button>
                  </div>
                  </>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "Activity" && (
            <div style={{ maxWidth: 700 }}>
              {activities.length === 0 && <div style={{ textAlign: "center", paddingTop: 60, color: T.muted, fontSize: 13 }}>No activity yet.</div>}
              {activities.map((act, i) => {
                const isLast = i === activities.length - 1;
                // Colour / icon per action type
                const typeConfig = {
                  created:          { bg: "#f0fdf4", border: "#16a34a", icon: "✦", color: "#16a34a" },
                  status_changed:   { bg: "#eff6ff", border: "#3b82f6", icon: "⇄", color: "#2563eb" },
                  priority_changed: { bg: "#fff7ed", border: "#f97316", icon: "▲", color: "#ea580c" },
                  assigned:         { bg: "#fdf4ff", border: "#a855f7", icon: "👤", color: "#9333ea" },
                  assigned_group:   { bg: "#f0f9ff", border: "#0ea5e9", icon: "⊞", color: "#0284c7" },
                  field_updated:    { bg: "#f9fafb", border: "#6b7280", icon: "✎", color: "#4b5563" },
                  commented:        { bg: "#fefce8", border: "#ca8a04", icon: "💬", color: "#b45309" },
                }[act.actionType] || { bg: "#f9fafb", border: "#d1d5db", icon: "·", color: "#6b7280" };
                return (
                  <div key={act.id} style={{ display: "flex", gap: 0, position: "relative" }}>
                    <div style={{ width: 110, flexShrink: 0, paddingTop: 3, textAlign: "right", paddingRight: 12 }}>
                      <span style={{ fontSize: 11, color: T.muted, lineHeight: 1.4 }}>{act.time}</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginRight: 12, flexShrink: 0 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: typeConfig.bg, border: `2px solid ${typeConfig.border}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, zIndex: 1, fontSize: 12 }}>
                        {typeConfig.icon}
                      </div>
                      {!isLast && <div style={{ width: 1.5, flex: 1, minHeight: 18, background: T.border, marginTop: 2, marginBottom: -2 }} />}
                    </div>
                    <div style={{ flex: 1, paddingBottom: 18 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1A202C", marginBottom: 4 }}>{act.actor}</div>
                      <div style={{ background: typeConfig.bg, border: `1px solid ${typeConfig.border}22`, borderRadius: 8, padding: "9px 12px" }}>
                        <div style={{ fontSize: 12, color: "#2D3748" }}>{act.text}</div>
                        {act.oldValue && act.newValue && (
                          <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 6, fontSize: 11 }}>
                            <span style={{ background: "#fee2e2", color: "#b91c1c", padding: "1px 7px", borderRadius: 4, fontWeight: 600 }}>{act.oldValue}</span>
                            <span style={{ color: T.muted }}>→</span>
                            <span style={{ background: "#dcfce7", color: "#15803d", padding: "1px 7px", borderRadius: 4, fontWeight: 600 }}>{act.newValue}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {!["Details", "Activity"].includes(tab) && (
            <div style={{ textAlign: "center", paddingTop: 80, color: T.muted }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#2D3748", marginBottom: 6 }}>{tab}</div>
              <div style={{ fontSize: 12 }}>No data available for this section.</div>
            </div>
          )}
        </div>

        {/* RIGHT SIDEBAR + ICON BAR */}
        <div style={{ display: "flex", flexDirection: "row", flexShrink: 0, borderLeft: `1px solid ${T.border}` }}>

          {/* Scrollable panel content */}
          <div style={{ width: 268, overflowY: "auto", background: "#FFFFFF" }}>

            {/* SUMMARY PANEL */}
            <div id="panel-summary" style={cardStyle("summary")}>
              <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", padding: "2px 7px", borderRadius: 4 }}>MY</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: "#2D3748" }}>My Team</span>
              </div>
              <div style={{ padding: "12px 14px" }}>
                <div style={{ fontSize: 17, fontWeight: 800, color: sc.color, marginBottom: 10 }}>{localListing.status}</div>
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Priority</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: "50%", background: getPriorityColor(localListing.priority), display: "inline-block" }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{localListing.priority || "Low"}</span>
                  </div>
                </div>
                {slaData && slaData.policy_name && (
                  <div style={{ marginBottom: 8, fontSize: 11, color: '#718096', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ background: '#F0F2F5', padding: '1px 6px', borderRadius: 4, fontWeight: 600 }}>{slaData.policy_name}</span>
                  </div>
                )}
                <div style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>First Response Due</div>
                  {slaData && slaData.first_response_due_at ? (() => {
                    const cd = formatDueCountdown(slaData.first_response_due_at);
                    return (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#2D3748" }}>by {formatInTz(slaData.first_response_due_at, { weekday: 'short', day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}</span>
                        {cd && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 700, whiteSpace: "nowrap", ...cd.style }}>{cd.label}</span>}
                      </div>
                    );
                  })() : (
                    <div style={{ fontSize: 12, color: "#718096" }}>Not set</div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>Resolution Due</div>
                  {slaData && slaData.resolution_due_at ? (() => {
                    const cd = formatDueCountdown(slaData.resolution_due_at);
                    return (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#2D3748" }}>by {formatInTz(slaData.resolution_due_at, { weekday: 'short', day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true })}</span>
                        {cd && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, fontWeight: 700, whiteSpace: "nowrap", ...cd.style }}>{cd.label}</span>}
                      </div>
                    );
                  })() : (
                    <div style={{ fontSize: 12, color: "#718096" }}>Not set</div>
                  )}
                </div>
              </div>
            </div>

            {/* REQUESTER PANEL */}
            <div id="panel-requester" style={cardStyle("requester")}>
              <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 6 }}>
                <Icon name="user" size={13} color={T.muted} />
                <span style={{ fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Requester Information</span>
              </div>
              <div style={{ padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
                  <Avatar name={localListing.requestor || "R"} size={34} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: T.active, cursor: "pointer" }}>{localListing.requestor || localListing.email || "—"}</div>
                    {localListing.email && <div style={{ fontSize: 11, color: T.muted }}>{localListing.email}</div>}
                  </div>
                </div>
                <div onClick={() => setShowRequesterModal(true)} style={{ fontSize: 12, color: T.active, cursor: "pointer", fontWeight: 600, marginBottom: 6, textDecoration: "underline" }}>View more details</div>
                <div onClick={() => { setMeetingForm(f => ({ ...f, title: localListing.subject || localListing.description || '' })); setShowScheduleModal(true); }} style={{ fontSize: 12, color: T.active, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="calendar" size={12} color={T.active} /> Schedule meeting with Requester
                </div>
              </div>
            </div>

            {/* COLLABORATE PANEL */}
            <div id="panel-collab" style={cardStyle("collab")}>
              <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="layers" size={13} color={T.muted} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Collaborate</span>
                </div>
                <Icon name="chevronU" size={13} color={T.muted} />
              </div>
              <div style={{ padding: "12px 14px" }}>
                <button style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 8, background: "#FFFFFF", cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#2D3748" }}>
                  <span style={{ fontSize: 16 }}>🟦</span> Teams
                  <Icon name="chevronD" size={12} color={T.muted} style={{ marginLeft: "auto" }} />
                </button>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, padding: "8px 10px", background: "#eff6ff", borderRadius: 7, border: "1px solid #bfdbfe" }}>
                  <Icon name="info" size={14} color="#2563eb" />
                  <div style={{ fontSize: 11, color: "#1d4ed8", lineHeight: 1.5 }}>Please upgrade to the latest version of <span style={{ fontWeight: 700 }}>Servicebot</span> to use collaboration.</div>
                </div>
              </div>
            </div>

            {/* PROPERTIES PANEL */}
            <div id="panel-props" style={cardStyle("props")}>
              <div style={{ padding: "10px 14px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="settings" size={13} color={T.muted} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Properties</span>
                </div>
                <Icon name="chevronU" size={13} color={T.muted} />
              </div>
              <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                {/* Priority */}
                <div>
                  {propLabel('Priority', 'priority', true)}
                  <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", width: 9, height: 9, borderRadius: "50%", background: getPriorityColor(props.priority), display: "inline-block", zIndex: 1 }} />
                    <select value={props.priority} onChange={e => setProp({ priority: e.target.value })} style={{ ...propFieldStyle('priority'), paddingLeft: 24 }}>
                      {(mdOptions("priority").length ? mdOptions("priority") : ["Low", "Medium", "High", "Urgent"]).map(o => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                </div>
                {/* Status */}
                <div>
                  {propLabel('Status', 'status', true)}
                  <select value={props.status} onChange={e => setProp({ status: e.target.value })} style={propFieldStyle('status')}>
                    {STATUS_OPTIONS.map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Source */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Source</label>
                  <select value={props.source} onChange={e => setProps(p => ({ ...p, source: e.target.value }))} style={selectStyle}>
                    {(mdOptions("source").length ? mdOptions("source") : ["Email", "Portal", "Phone", "Chat"]).map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Type */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Type</label>
                  <select value={props.type} onChange={e => setProps(p => ({ ...p, type: e.target.value }))} style={selectStyle}>
                    {(mdOptions("type").length ? mdOptions("type") : ["Incident", "Service Request", "Problem", "Change"]).map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Group */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Group</label>
                  <select
                    value={props.groupId ?? ""}
                    onChange={e => {
                      const gid = e.target.value ? Number(e.target.value) : null;
                      const grp = groups.find(g => g.id === gid);
                      setProps(p => ({ ...p, groupId: gid, group: grp ? grp.name : "" }));
                    }}
                    style={selectStyle}
                  >
                    <option value="">-- Select Group --</option>
                    {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </div>
                {/* Agent */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Agent</label>
                  <select
                    value={props.assignedToId ?? ""}
                    onChange={e => {
                      const agentId = e.target.value ? Number(e.target.value) : null;
                      const agent = agents.find(a => a.id === agentId);
                      setProps(p => ({ ...p, assignedToId: agentId, assignedTo: agent ? agent.name : "" }));
                    }}
                    style={selectStyle}
                  >
                    <option value="">-- Select Agent --</option>
                    {agents.map(a => {
                      const me = a.id === JSON.parse(localStorage.getItem("user") || "{}").id;
                      return <option key={a.id} value={a.id}>{a.name}{me ? " (Me)" : ""}</option>;
                    })}
                  </select>
                </div>
                {/* Department */}
                <div>
                  {propLabel('Department', 'dept', true)}
                  <select value={props.dept} onChange={e => setProp({ dept: e.target.value })} style={propFieldStyle('dept')}>
                    <option value="">--</option>
                    {(mdOptions("department").length ? mdOptions("department") : ["IT", "HR", "Finance", "Engineering"]).map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Organization Unit */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Organization Unit</label>
                  <select value={props.orgUnit} onChange={e => setProps(p => ({ ...p, orgUnit: e.target.value }))} style={selectStyle}>
                    <option value="">—</option>
                    {(mdOptions("org_unit").length ? mdOptions("org_unit") : ["Headquarters", "Regional Office", "Branch Office", "Data Center", "Field Operations", "Remote Site"]).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {/* Head of Department */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Head of Department</label>
                  <select value={props.headOfDept} onChange={e => setProps(p => ({ ...p, headOfDept: e.target.value }))} style={selectStyle}>
                    <option value="">—</option>
                    {(mdOptions("head_of_department").length ? mdOptions("head_of_department") : ["CIO", "CFO", "CHRO", "CTO", "COO", "Head of Engineering", "Head of Sales", "Head of Marketing"]).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                {/* Category */}
                <div>
                  {propLabel('Category', 'category', true)}
                  <select value={props.category} onChange={e => setProp({ category: e.target.value, subCategory: '', additionalSubCategory: '' })} style={propFieldStyle('category')}>
                    <option value="">--</option>
                    {(mdOptions("category").length ? mdOptions("category") : ["Hardware", "Software", "Network"]).map(o => <option key={o}>{o}</option>)}
                  </select>
                </div>
                {/* Sub-Category */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Sub-Category</label>
                  <select value={props.subCategory} onChange={e => setProps(p => ({ ...p, subCategory: e.target.value, additionalSubCategory: '' }))} style={selectStyle}>
                    <option value="">--</option>
                    {(masterData['sub_category'] || [])
                      .filter(d => !props.category || !d.parent_value || d.parent_value === props.category)
                      .map(d => <option key={d.id} value={d.value}>{d.value}</option>)}
                  </select>
                </div>
                {/* Additional Sub-Category */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Additional Sub-Category</label>
                  <select value={props.additionalSubCategory} onChange={e => setProps(p => ({ ...p, additionalSubCategory: e.target.value }))} style={selectStyle}>
                    <option value="">--</option>
                    {(masterData['additional_sub_category'] || [])
                      .filter(d => !props.subCategory || !d.parent_value || d.parent_value === props.subCategory)
                      .map(d => <option key={d.id} value={d.value}>{d.value}</option>)}
                  </select>
                </div>
                {/* End User */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>End User</label>
                  <input type="text" value={props.endUser} onChange={e => setProps(p => ({ ...p, endUser: e.target.value }))} style={{ ...selectStyle, cursor: 'text' }} placeholder="—" />
                </div>
                {/* Remarks */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Remarks</label>
                  <textarea value={props.remarks} onChange={e => setProps(p => ({ ...p, remarks: e.target.value }))} rows={3} style={{ ...selectStyle, height: 'auto', resize: 'vertical', cursor: 'text', padding: '6px 10px' }} placeholder="—" />
                </div>
                {/* Request Rating */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Request Rating (1–5)</label>
                  <input type="number" min="1" max="5" value={props.requestRating} onChange={e => setProps(p => ({ ...p, requestRating: e.target.value }))} style={{ ...selectStyle, cursor: 'text' }} placeholder="—" />
                </div>
                {/* Procurement Completion Date */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Procurement Completion Date</label>
                  <input type="date" value={props.procurementCompletionDate ? String(props.procurementCompletionDate).substring(0, 10) : ""} onChange={e => setProps(p => ({ ...p, procurementCompletionDate: e.target.value }))} style={{ ...selectStyle, cursor: 'text' }} />
                </div>
                {/* Reason for Breach */}
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Reason for Breach</label>
                  <textarea value={props.reasonForBreach} onChange={e => setProps(p => ({ ...p, reasonForBreach: e.target.value }))} rows={2} style={{ ...selectStyle, height: 'auto', resize: 'vertical', cursor: 'text', padding: '6px 10px' }} placeholder="—" />
                </div>
                {/* Action Takers — auto-populated, read-only */}
                {(localListing.actionTaker1 || localListing.actionTaker2 || localListing.actionTaker3) && (
                  <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: T.muted, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Action Takers</div>
                    {[1, 2, 3].map(n => {
                      const name    = localListing[`actionTaker${n}`];
                      const remarks = localListing[`actionTaker${n}Remarks`];
                      const ts      = localListing[`actionTaker${n}Timestamp`];
                      if (!name) return null;
                      return (
                        <div key={n} style={{ marginBottom: 8, padding: '8px 10px', background: T.g50, borderRadius: 6, border: `1px solid ${T.border}` }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#1A202C' }}>
                              <span style={{ fontSize: 9, fontWeight: 700, color: T.g700, background: T.g100, borderRadius: 4, padding: '1px 5px', marginRight: 5 }}>AT{n}</span>{name}
                            </span>
                            {ts && <span style={{ fontSize: 10, color: T.muted }}>{ts}</span>}
                          </div>
                          {remarks && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#4A5568', lineHeight: 1.4, wordBreak: 'break-word' }}>{remarks}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* First Response Time — read-only */}
                {localListing.firstResponseTime && (
                  <div style={{ fontSize: 11, color: T.muted, padding: '4px 0' }}>
                    <span style={{ fontWeight: 700 }}>First Response: </span>{localListing.firstResponseTime}
                  </div>
                )}
                {saveFeedback && (
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    background: saveFeedback.type === 'success' ? '#ECFDF5'
                      : saveFeedback.type === 'warning' ? '#FFFBEB'
                      : '#FEF2F2',
                    border: `1px solid ${
                      saveFeedback.type === 'success' ? '#A7F3D0'
                      : saveFeedback.type === 'warning' ? '#FDE68A'
                      : '#FECACA'
                    }`,
                    color: saveFeedback.type === 'success' ? '#065F46'
                      : saveFeedback.type === 'warning' ? '#92400E'
                      : '#991B1B',
                  }}>
                    {saveFeedback.type === 'success' ? '✓ ' : saveFeedback.type === 'warning' ? '⚠ ' : '✕ '}
                    {saveFeedback.message}
                  </div>
                )}
                <button onClick={handlePropsUpdate} disabled={saving}
                  style={{ width: "100%", padding: "9px 0", borderRadius: 8, border: "none", background: saving ? "#a7f3d0" : "#6366f1", color: "#fff", fontSize: 13, fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", marginTop: 2 }}>
                  {saving ? "Saving..." : "Update"}
                </button>
              </div>
            </div>

            {/* TIME ENTRIES PANEL */}
            <div id="panel-time" style={{ ...cardStyle("time"), marginBottom: 16 }}>
              <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Icon name="calendar" size={13} color={T.muted} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#2D3748" }}>Time entries</span>
                </div>
                <span style={{
                  fontSize: 13, fontWeight: 800, fontFamily: 'monospace',
                  color: localListing.timer_status === 'running' ? '#3B5BDB' : '#1A202C',
                }}>
                  {formatMsShort(
                    (localListing.timer_status === 'running' || localListing.timer_status === 'awaiting_pause') && localListing.timer_started_at
                      ? getLiveElapsedMs(localListing.timer_elapsed_ms, localListing.timer_started_at, now)
                      : localListing.timer_elapsed_ms
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* ── REQUESTER DETAILS MODAL ─────────────────────────────────── */}
          {showRequesterModal && (() => {
            const reqUser = recipientUsers.find(u => u.email && localListing.email && u.email.toLowerCase() === localListing.email.toLowerCase());
            const relatedTickets = allListings.filter(t => t.email && localListing.email && t.email.toLowerCase() === localListing.email.toLowerCase() && t.id !== localListing.id);
            return (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowRequesterModal(false)}>
                <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: 480, maxWidth: "95vw", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
                  {/* Header */}
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 15, fontWeight: 800, color: "#1A202C" }}>Requester Details</span>
                    <button onClick={() => setShowRequesterModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: T.muted, lineHeight: 1 }}>✕</button>
                  </div>
                  {/* Body */}
                  <div style={{ padding: "20px", overflowY: "auto", flex: 1 }}>
                    {/* Avatar + name */}
                    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                      <Avatar name={localListing.requestor || "R"} size={52} />
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: "#1A202C" }}>{localListing.requestor || reqUser?.name || "—"}</div>
                        <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>{localListing.email || "No email"}</div>
                        {reqUser?.role && <div style={{ marginTop: 4, display: "inline-block", fontSize: 11, fontWeight: 700, background: "#EEF2FF", color: T.active, borderRadius: 6, padding: "2px 8px" }}>{reqUser.role}</div>}
                      </div>
                    </div>
                    {/* Info rows */}
                    {[
                      { label: "Phone",      value: reqUser?.phone || "—" },
                      { label: "Company",    value: reqUser?.company || "—" },
                      { label: "Department", value: reqUser?.department || localListing.dept || "—" },
                    ].map(({ label, value }) => (
                      <div key={label} style={{ display: "flex", marginBottom: 10, fontSize: 13 }}>
                        <span style={{ width: 110, fontWeight: 700, color: T.muted, flexShrink: 0 }}>{label}</span>
                        <span style={{ color: "#2D3748" }}>{value}</span>
                      </div>
                    ))}
                    {/* Related tickets */}
                    {relatedTickets.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: T.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.05em" }}>Other Tickets ({relatedTickets.length})</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {relatedTickets.slice(0, 5).map(t => {
                            const rawDesc = t.subject || t.description || '';
                            const plainDesc = rawDesc.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() || '—';
                            return (
                              <div key={t.id}
                                onClick={() => { window.open('/tickets/' + t.id, '_blank', 'noopener,noreferrer'); }}
                                style={{ padding: "8px 12px", background: T.g50, borderRadius: 8, border: `1px solid ${T.border}`, fontSize: 12, cursor: "pointer", transition: "background 0.15s" }}
                                onMouseEnter={e => e.currentTarget.style.background = "#E0E7FF"}
                                onMouseLeave={e => e.currentTarget.style.background = T.g50}
                              >
                                <div style={{ fontWeight: 700, color: T.active, textDecoration: "underline" }}>{t.id}</div>
                                <div style={{ color: "#2D3748", marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{plainDesc}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── SCHEDULE MEETING MODAL ───────────────────────────────────── */}
          {showScheduleModal && (() => {
            const handleMeetingSubmit = () => {
              const { title, date, time, duration, notes } = meetingForm;
              if (!date) { toast("Please select a meeting date.", 'warning'); return; }
              // Build Google Calendar URL
              const start = new Date(`${date}T${time}:00`);
              const end   = new Date(start.getTime() + parseInt(duration, 10) * 60000);
              const fmt = d => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
              const details = notes ? encodeURIComponent(notes) : '';
              const requesterEmail = localListing.email || '';
              const gcUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE` +
                `&text=${encodeURIComponent(title || 'Meeting')}` +
                `&dates=${fmt(start)}/${fmt(end)}` +
                `&details=${details}` +
                `&add=${encodeURIComponent(requesterEmail)}`;
              window.open(gcUrl, '_blank', 'noopener,noreferrer');
            };
            const inputSt = { width: "100%", padding: "8px 10px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, color: "#2D3748", outline: "none", boxSizing: "border-box", background: "#fff" };
            return (
              <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 900, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setShowScheduleModal(false)}>
                <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", width: 460, maxWidth: "95vw" }}>
                  {/* Header */}
                  <div style={{ padding: "16px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Icon name="calendar" size={15} color={T.active} />
                      <span style={{ fontSize: 15, fontWeight: 800, color: "#1A202C" }}>Schedule Meeting</span>
                    </div>
                    <button onClick={() => setShowScheduleModal(false)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, color: T.muted, lineHeight: 1 }}>✕</button>
                  </div>
                  {/* Form */}
                  <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 14 }}>
                    <div style={{ fontSize: 12, color: T.muted }}>Scheduling with: <strong style={{ color: "#2D3748" }}>{localListing.requestor || localListing.email || "Requester"}</strong></div>
                    {/* Title */}
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Meeting Title</label>
                      <input style={inputSt} value={meetingForm.title} onChange={e => setMeetingForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Discuss issue with requester" />
                    </div>
                    {/* Date + Time */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Date <span style={{ color: "#ef4444" }}>*</span></label>
                        <input type="date" style={inputSt} value={meetingForm.date} onChange={e => setMeetingForm(f => ({ ...f, date: e.target.value }))} min={new Date().toISOString().split('T')[0]} />
                      </div>
                      <div>
                        <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Time</label>
                        <input type="time" style={inputSt} value={meetingForm.time} onChange={e => setMeetingForm(f => ({ ...f, time: e.target.value }))} />
                      </div>
                    </div>
                    {/* Duration */}
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Duration</label>
                      <select style={inputSt} value={meetingForm.duration} onChange={e => setMeetingForm(f => ({ ...f, duration: e.target.value }))}>
                        {[["15","15 minutes"],["30","30 minutes"],["45","45 minutes"],["60","1 hour"],["90","1.5 hours"],["120","2 hours"]].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                      </select>
                    </div>
                    {/* Notes */}
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4 }}>Notes / Agenda</label>
                      <textarea rows={3} style={{ ...inputSt, resize: "vertical" }} value={meetingForm.notes} onChange={e => setMeetingForm(f => ({ ...f, notes: e.target.value }))} placeholder="Meeting agenda or notes..." />
                    </div>
                    {/* Actions */}
                    <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 4 }}>
                      <button onClick={() => setShowScheduleModal(false)} style={{ padding: "8px 18px", borderRadius: 8, border: `1px solid ${T.border}`, background: "#fff", color: "#2D3748", fontSize: 13, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                      <button onClick={handleMeetingSubmit} style={{ padding: "8px 18px", borderRadius: 8, border: "none", background: T.active, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                        <Icon name="calendar" size={13} color="#fff" /> Open in Google Calendar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Vertical icon bar */}
          <div style={{ width: 42, borderLeft: `1px solid ${T.border}`, background: "#FFFFFF", display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 10, gap: 4, flexShrink: 0 }}>
            {PANEL_ICONS.map(({ key, icon, title }) => {
              const isActive = activePanel === key;
              return (
                <button
                  key={key}
                  onClick={() => handleIconClick(key)}
                  title={title}
                  style={{
                    width: 32, height: 32, border: "none", borderRadius: 8,
                    background: isActive ? T.active : "transparent",
                    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.15s",
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = T.g50; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                >
                  <Icon name={icon} size={16} color={isActive ? "#fff" : T.muted} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
function Th({ label, sortKey, currentSortKey, sortDir, onSort, width }) {
  const isActive = currentSortKey === sortKey;
  return (
    <th onClick={() => sortKey && onSort(sortKey)} style={{ padding: "0 10px", fontSize: 10, fontWeight: 700, color: T.muted, textAlign: "left", borderBottom: `1.5px solid ${T.border}`, height: 36, whiteSpace: "nowrap", cursor: sortKey ? "pointer" : "default", userSelect: "none", width, textTransform: "uppercase", letterSpacing: "0.04em", background: T.g50 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
        {label}
      </div>
    </th>
  );
}

// ─── Column definitions ────────────────────────────────────────────────────────
const ALL_COLUMNS = [
  { id: "requestNo",   label: "Request No.",             defaultOn: true,  fixed: true },
  { id: "description", label: "Problem Description",     defaultOn: true  },
  { id: "state",       label: "State",                   defaultOn: true  },
  { id: "status",      label: "Current Status",          defaultOn: true  },
  { id: "sla",         label: "SLA",                    defaultOn: true  },
  { id: "assignee",    label: "Action Taker / Assignee", defaultOn: true  },
  { id: "requestor",   label: "Requestor",               defaultOn: true  },
  { id: "dept",        label: "Department",              defaultOn: true  },
  { id: "priority",    label: "Priority",                defaultOn: true  },
  { id: "category",    label: "Category",                defaultOn: true  },
  { id: "subCategory", label: "Sub-Category",            defaultOn: true  },
  { id: "created",     label: "Created Date",            defaultOn: true  },
  { id: "modified",    label: "Last Modified",           defaultOn: false },
  { id: "orgUnit",     label: "Organization Unit",       defaultOn: false },
  { id: "headOfDept",  label: "Head of Department",      defaultOn: false },
  { id: "addlSubCat",  label: "Additional Sub-Category", defaultOn: false },
  { id: "actionTaker", label: "Action Taker",            defaultOn: false },
  { id: "at1Remarks",  label: "AT1 Remarks",             defaultOn: false },
  { id: "at2",         label: "Action Taker 2",          defaultOn: false },
  { id: "at3",         label: "Action Taker 3",          defaultOn: false },
  { id: "endUser",     label: "End User",                defaultOn: false },
  { id: "slaStatus",   label: "SLA Status",              defaultOn: false },
];

const COL_STORAGE_KEY = "ticket_visible_cols";

function loadVisibleCols() {
  try {
    const raw = localStorage.getItem(COL_STORAGE_KEY);
    if (raw) {
      const cols = JSON.parse(raw);
      if (!cols.includes('state')) {
        const idx = cols.indexOf('description');
        const at = idx >= 0 ? idx + 1 : 1;
        return [...cols.slice(0, at), 'state', ...cols.slice(at)];
      }
      return cols;
    }
  } catch {}
  return ALL_COLUMNS.filter(c => c.defaultOn).map(c => c.id);
}

// ─── Column Picker Panel ───────────────────────────────────────────────────────
function ColPicker({ visible, onChange, onClose }) {
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const toggle = (id) => {
    const col = ALL_COLUMNS.find(c => c.id === id);
    if (col?.fixed) return;
    const next = visible.includes(id) ? visible.filter(x => x !== id) : [...visible, id];
    onChange(next);
  };

  const selectAll  = () => onChange(ALL_COLUMNS.map(c => c.id));
  const resetDef   = () => onChange(ALL_COLUMNS.filter(c => c.defaultOn).map(c => c.id));

  return (
    <div ref={ref} style={{
      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 200,
      background: "#FFFFFF", border: `1px solid ${T.border}`, borderRadius: 12,
      boxShadow: "0 12px 40px rgba(0,0,0,0.15)", width: 260, overflow: "hidden",
    }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: "#1A202C" }}>Column Visibility</span>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={selectAll} style={{ fontSize: 11, fontWeight: 700, color: T.active, border: "none", background: "none", cursor: "pointer" }}>All</button>
          <button onClick={resetDef}  style={{ fontSize: 11, fontWeight: 700, color: T.muted,   border: "none", background: "none", cursor: "pointer" }}>Reset</button>
        </div>
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto", padding: "6px 0" }}>
        {ALL_COLUMNS.map(col => {
          const checked = visible.includes(col.id);
          return (
            <label key={col.id} style={{
              display: "flex", alignItems: "center", gap: 10, padding: "8px 16px",
              cursor: col.fixed ? "default" : "pointer",
              background: "transparent",
              opacity: col.fixed ? 0.5 : 1,
            }}
              onMouseEnter={e => { if (!col.fixed) e.currentTarget.style.background = T.g50; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            >
              <div onClick={() => toggle(col.id)} style={{
                width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                border: `2px solid ${checked ? T.active : T.border}`,
                background: checked ? T.active : "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}>
                {checked && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
              </div>
              <span style={{ fontSize: 12.5, fontWeight: checked ? 700 : 500, color: checked ? "#1A202C" : T.muted }}>{col.label}</span>
              {col.fixed && <span style={{ fontSize: 10, color: T.muted, marginLeft: "auto" }}>fixed</span>}
            </label>
          );
        })}
      </div>
      <div style={{ padding: "10px 16px", borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.muted, textAlign: "center" }}>
        {visible.length} of {ALL_COLUMNS.length} columns visible
      </div>
    </div>
  );
}

function buildPendingBannerMessage({ new: newCount, updated: updatedCount }) {
  const parts = [];
  if (newCount > 0) parts.push(`${newCount} new ticket${newCount !== 1 ? 's' : ''}`);
  if (updatedCount > 0) parts.push(`${updatedCount} ticket${updatedCount !== 1 ? 's' : ''} updated`);
  if (!parts.length) return '';
  return `${parts.join(', ')} — click to refresh`;
}

export default function ListingsPage({ listings = [], setListings, pendingChanges = { new: 0, updated: 0 }, onRefreshTickets }) {
  const pendingNew = pendingChanges?.new || 0;
  const pendingUpdated = pendingChanges?.updated || 0;
  const pendingTotal = pendingNew + pendingUpdated;
  const pendingBannerText = buildPendingBannerMessage(pendingChanges);
  const size = useWindowSizeSafe();
  const isMobile = size < 768;
  const [search, setSearch] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterStatus, setFilterStatus] = useState("All");
  const [filterPriority, setFilterPriority] = useState("All");
  const [filterAgent, setFilterAgent] = useState("All");
  const [agentFilterOptions, setAgentFilterOptions] = useState([{ value: "All", label: "All agents" }]);
  const [urlFilters, setUrlFilters] = useState({});
  const [sortKey, setSortKey] = useState("created");
  const [sortDir, setSortDir] = useState("desc");
  const [selected, setSelected] = useState([]);
  const [starred, setStarred] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [visibleCols, setVisibleCols] = useState(loadVisibleCols);
  const [showColPicker, setShowColPicker] = useState(false);
  const colPickerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const ticketId = location.pathname.startsWith('/tickets/') ? location.pathname.slice('/tickets/'.length) : null;
  const detailListing = ticketId ? listings.find(l => l.id === ticketId) || null : null;
  const [showExport, setShowExport] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const statuses = ["All", ...STATUS_OPTIONS];

  useEffect(() => {
    usersAPI.list()
      .then(data => {
        const agents = (data || []).filter(u => (u.role || "").toLowerCase() !== "requester");
        setAgentFilterOptions([
          { value: "All", label: "All agents" },
          { value: "__unassigned__", label: "Unassigned" },
          ...agents
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
            .map(a => ({ value: String(a.id), label: a.name || a.email || `Agent #${a.id}` })),
        ]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const next = {};
    const status = searchParams.get('status');
    const viewParam = searchParams.get('view');
    const priority = searchParams.get('priority');
    const assignee = searchParams.get('assignee');
    if (status && STATUS_OPTIONS.includes(status)) {
      setFilterStatus(status);
      setShowFilters(true);
    } else if (status && status !== 'All') {
      // Accept DB-level statuses and comma-separated multi-status (e.g. from dashboard)
      setFilterStatus(status);
      setShowFilters(true);
    } else if (viewParam === 'active' || viewParam === 'unresolved') {
      setFilterStatus('All');
      next.view = viewParam;
      setShowFilters(true);
    } else if (!searchParams.toString()) {
      setFilterStatus('All');
    }
    if (priority) {
      next.priority = priority;
      setFilterPriority(priority);
    } else {
      setFilterPriority("All");
    }
    if (assignee) {
      next.assignee = assignee;
      setFilterAgent(assignee);
    } else {
      setFilterAgent("All");
    }
    const category = searchParams.get('category');
    if (category) next.category = category;
    if (searchParams.get('unassigned') === '1') next.unassigned = true;
    if (searchParams.get('overdue') === '1') next.overdue = true;
    if (searchParams.get('dueToday') === '1') next.dueToday = true;
    const department = searchParams.get('department');
    if (department) next.department = department;
    const source = searchParams.get('source');
    if (source) next.source = source;
    const ticketType = searchParams.get('type');
    if (ticketType) next.ticketType = ticketType;
    const slaParam = searchParams.get('sla_status');
    if (slaParam === 'breached') next.slaBreached = true;
    else if (slaParam === 'at_risk') next.slaAtRisk = true;
    else if (slaParam === 'within') next.slaWithin = true;
    const fromDate = searchParams.get('from') || '';
    const toDate   = searchParams.get('to')   || '';
    if (fromDate) next.fromDate = fromDate;
    if (toDate)   next.toDate   = toDate;
    setUrlFilters(next);
    setPage(1);
  }, [searchParams]);

  const clearUrlFilters = () => {
    setUrlFilters({});
    setFilterStatus('All');
    setFilterPriority('All');
    setFilterAgent('All');
    setSearchParams({});
    setPage(1);
  };

  const handleFilterAgentChange = (val) => {
    setFilterAgent(val);
    setPage(1);
    if (val === 'All') {
      setUrlFilters(f => {
        const next = { ...f };
        delete next.assignee;
        delete next.unassigned;
        return next;
      });
    } else if (val === '__unassigned__') {
      setUrlFilters(f => ({ ...f, assignee: undefined, unassigned: true }));
    } else {
      const opt = agentFilterOptions.find(o => o.value === val);
      setUrlFilters(f => ({ ...f, unassigned: false, assignee: opt?.label || val }));
    }
  };

  const handleFilterPriorityChange = (val) => {
    setFilterPriority(val);
    setPage(1);
    setUrlFilters(f => {
      const next = { ...f };
      if (val === 'All') delete next.priority;
      else next.priority = val;
      return next;
    });
  };

  const handleFilterStatusChange = (val) => {
    setFilterStatus(val);
    setPage(1);
    if (val !== 'All') setUrlFilters(f => ({ ...f, view: undefined }));
  };

  const activeFilterLabels = [
    filterStatus !== 'All' ? `Status: ${filterStatus}` : null,
    filterPriority !== 'All' ? `Priority: ${filterPriority}` : null,
    filterAgent === '__unassigned__' ? 'Unassigned' : filterAgent !== 'All'
      ? `Agent: ${agentFilterOptions.find(o => o.value === filterAgent)?.label || filterAgent}`
      : null,
    urlFilters.view === 'active' ? 'Active tickets' : null,
    urlFilters.priority && filterPriority === 'All' ? `Priority: ${urlFilters.priority}` : null,
    urlFilters.assignee && filterAgent === 'All' ? `Assignee: ${urlFilters.assignee}` : null,
    urlFilters.category ? `Category: ${urlFilters.category}` : null,
    urlFilters.unassigned ? 'Unassigned' : null,
    urlFilters.overdue ? 'Overdue' : null,
    urlFilters.dueToday ? 'Due today' : null,
    urlFilters.department ? `Department: ${urlFilters.department}` : null,
    urlFilters.source ? `Source: ${urlFilters.source}` : null,
    urlFilters.ticketType ? `Type: ${urlFilters.ticketType}` : null,
    urlFilters.view === 'unresolved' ? 'Unresolved' : null,
    urlFilters.slaAtRisk ? 'SLA at risk' : null,
    urlFilters.slaWithin ? 'Within SLA' : null,
    urlFilters.fromDate || urlFilters.toDate ? `Period: ${urlFilters.fromDate || ''}–${urlFilters.toDate || ''}` : null,
    urlFilters.slaBreached ? 'Breached SLA' : null,
  ].filter(Boolean);

  const tdStyle = { padding: "16px 18px", fontSize: 13, color: "#1A202C", verticalAlign: "middle", lineHeight: 1.45 };
  const thStyle = { padding: "14px 18px", fontSize: 11.5, fontWeight: 800, color: "#2C41A8", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap", borderBottom: `2px solid ${T.border}`, userSelect: "none", lineHeight: 1.35 };

  const updateVisibleCols = (cols) => {
    setVisibleCols(cols);
    try { localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(cols)); } catch {}
  };

  const col = (id) => visibleCols.includes(id);

  const handleSortChange = (key, dir) => { setSortKey(key); setSortDir(dir); setPage(1); };
  const handleHeaderSort = key => { if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortKey(key); setSortDir("asc"); } };

  const filtered = listings
    .filter(l => {
      const q = search.toLowerCase();
      const matchesSearch = l.name.toLowerCase().includes(q) || l.vendor.toLowerCase().includes(q) || String(l.id).toLowerCase().includes(q);
      if (!matchesSearch) return false;
      if (filterStatus !== 'All') {
        const statusList = filterStatus.includes(',')
          ? filterStatus.split(',').map(s => s.trim())
          : [filterStatus];
        if (!statusList.includes(l.status)) return false;
      }
      if (urlFilters.view === 'active' && TERMINAL_STATUSES.includes(l.status)) return false;
      if (urlFilters.view === 'unresolved' && RESOLVED_STATUSES.includes(l.status)) return false;
      const priorityFilter = filterPriority !== 'All' ? filterPriority : urlFilters.priority;
      if (priorityFilter && l.priority !== priorityFilter) return false;
      if (filterAgent === '__unassigned__' || urlFilters.unassigned) {
        if (l._assigned_to_id || l.assignedTo) return false;
      } else if (filterAgent !== 'All') {
        const agentId = parseInt(filterAgent, 10);
        if (!Number.isNaN(agentId)) {
          if (Number(l._assigned_to_id) !== agentId) return false;
        } else {
          const name = (l.assignedTo || l.assignedAgent?.name || '').toLowerCase();
          const want = (agentFilterOptions.find(o => o.value === filterAgent)?.label || filterAgent).toLowerCase();
          if (name !== want) return false;
        }
      } else if (urlFilters.assignee) {
        const name = (l.assignedTo || l.assignedAgent?.name || '').toLowerCase();
        if (name !== urlFilters.assignee.toLowerCase()) return false;
      }
      if (urlFilters.category) {
        const lCat = (l.category || '').trim();
        const fCat = urlFilters.category.trim().toLowerCase();
        const isUncategorized = fCat === 'uncategorized';
        if (isUncategorized) {
          if (lCat !== '') return false;
        } else {
          if (lCat.toLowerCase() !== fCat) return false;
        }
      }
      if (urlFilters.unassigned && (l._assigned_to_id || l.assignedTo)) return false;
      if (urlFilters.overdue && !isOverdueTicket(l)) return false;
      if (urlFilters.dueToday && !isDueTodayTicket(l)) return false;
      if (urlFilters.department) {
        const lDept = (l.dept || '').trim();
        const fDept = urlFilters.department.trim();
        if (fDept.toLowerCase() === 'unspecified') {
          if (lDept !== '') return false;
        } else if (lDept.toLowerCase() !== fDept.toLowerCase()) {
          return false;
        }
      }
      if (urlFilters.source && (l.source || '').toLowerCase() !== urlFilters.source.toLowerCase()) return false;
      if (urlFilters.ticketType && (l.type || '').toLowerCase() !== urlFilters.ticketType.toLowerCase()) return false;
      if (urlFilters.fromDate) {
        const d = (l._created_at || '').toString().slice(0, 10);
        if (d && d < urlFilters.fromDate) return false;
      }
      if (urlFilters.toDate) {
        const d = (l._created_at || '').toString().slice(0, 10);
        if (d && d > urlFilters.toDate) return false;
      }
      if (urlFilters.slaBreached && l.slaStatus !== 'breached') return false;
      if (urlFilters.slaAtRisk && l.slaStatus !== 'at_risk') return false;
      if (urlFilters.slaWithin && l.slaStatus !== 'within') return false;
      return true;
    })
    .sort((a, b) => {
      let av = a[sortKey] ?? "";
      let bv = b[sortKey] ?? "";
      if (typeof av === "string") { av = av.toLowerCase(); bv = bv.toLowerCase(); }
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });

  const allSelected = filtered.length > 0 && filtered.every(l => selected.includes(l.id));
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage  = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * perPage, safePage * perPage);
  const toggleAll = () => setSelected(allSelected ? [] : filtered.map(l => l.id));
  const toggleOne = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleStar = id => setStarred(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const handleCreate = ticket => {
    // ticketsAPI.create already runs mapTicket(), use those field names
    const listing = {
      _id: ticket.id,
      id: ticket.ticket_number || `#${ticket.id}`,
      name: ticket.subject || '',
      description: ticket.description || '',
      requestor: ticket.requester || '',
      email: ticket.email || '',
      cc: '',
      dept: ticket.dept || '',
      category: ticket.category || '',
      subCategory: '',
      status: ticket.status || 'Open',
      priority: ticket.priority || 'Low',
      type: ticket.type || 'Incident',
      source: ticket.source || 'Portal',
      group: ticket.group || '',
      _assigned_group_id: ticket._assigned_group_id || null,
      _assigned_to_id: ticket._assigned_to_id || null,
      assignedTo: ticket.assignedTo || '',
      assignedAgent: ticket._assigned_to_id ? { id: ticket._assigned_to_id, name: ticket.assignedTo || '' } : null,
      created: ticket.created || '',
      _created_at: null,
      modified: ticket.modified || '',
      slaStatus: null,
      resolutionDueAt: null,
      firstResponseDueAt: null,
      due_by: ticket.due_by || null,
    };
    setListings(ls => [listing, ...ls]);
    setShowCreate(false);
  };
  const handleEdit = listing => { setEditListing(listing); navigate('/tickets'); };
  const handleSaveEdit = updated => { setListings(ls => ls.map(l => l.id === updated.id ? updated : l)); setEditListing(null); };
  const handleDelete = id => { setListings(ls => ls.filter(l => l.id !== id)); setSelected(s => s.filter(x => x !== id)); if (ticketId === id) navigate('/tickets'); };
  const bulkDelete = () => { setListings(ls => ls.filter(l => !selected.includes(l.id))); setSelected([]); };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#FFFFFF", position: "relative" }}>
      <div style={{ background: "#FFFFFF", borderBottom: `1px solid ${T.border}`, padding: isMobile ? "0 16px" : "0 24px", height: isMobile ? 54 : 60, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: T.active, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 6px rgba(22,163,74,0.3)" }}><Icon name="tag" size={18} color="#fff" /></div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: "#1A202C", letterSpacing: "-0.01em" }}>Service Request</span>
            {!isMobile && <span style={{ fontSize: 11, color: T.muted, fontWeight: 500 }}>Manage and track service requests</span>}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={() => setShowCreate(true)} style={{ height: 36, padding: isMobile ? "0 12px" : "0 18px", borderRadius: 8, border: "none", background: T.active, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, boxShadow: "0 4px 12px var(--accent-bg)", transition: "all 0.2s" }} onMouseEnter={e => e.currentTarget.style.background = "var(--accent-dark)"} onMouseLeave={e => e.currentTarget.style.background = "var(--accent)"}><Icon name="plus" size={14} color="#fff" />{!isMobile && "Create Request"}</button>
        </div>
      </div>

      <div style={{ borderBottom: `1px solid ${T.border}`, background: "#FFFFFF", flexShrink: 0 }}>
        <div style={{ padding: isMobile ? "12px" : "12px 24px", display: "flex", flexDirection: isMobile ? "column" : "row", gap: 12, alignItems: isMobile ? "stretch" : "center" }}>
          <div style={{ flex: 1, height: 40, background: T.g50, border: `1.5px solid ${T.border}`, borderRadius: 10, padding: "0 14px", display: "flex", alignItems: "center", gap: 10, transition: "all 0.2s" }}> <Icon name="search" size={16} color={T.muted} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by request number, requestor or description..." style={{ border: "none", background: "transparent", fontSize: 13, color: "#1A202C", outline: "none", width: "100%", fontFamily: "inherit" }} /></div>
        </div>
      </div>

      <div style={{ background: "#FFFFFF", borderBottom: `1px solid ${T.border}`, padding: isMobile ? "8px 16px" : "0 24px", minHeight: 44, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 12.5, color: T.muted, fontWeight: 500 }}>
            {filtered.length > 0
              ? `${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, filtered.length)} of ${filtered.length} request${filtered.length !== 1 ? "s" : ""}`
              : "0 requests"}
            {selected.length > 0 && <span style={{ fontWeight: 700, color: T.active }}> &middot; {selected.length} selected</span>}
          </span>
          <div style={{ width: 1, height: 20, background: T.border }} />
          <SortByDropdown sortKey={sortKey} sortDir={sortDir} onSortChange={handleSortChange} />
          <div style={{ width: 1, height: 20, background: T.border }} />
          <ToolbarFilterSelect
            label="Agent"
            value={filterAgent}
            onChange={handleFilterAgentChange}
            options={agentFilterOptions}
            minWidth={140}
          />
          <ToolbarFilterSelect
            label="Status"
            value={filterStatus}
            onChange={handleFilterStatusChange}
            options={statuses.map(s => ({ value: s, label: s === 'All' ? 'All statuses' : s }))}
            minWidth={150}
          />
          <ToolbarFilterSelect
            label="Priority"
            value={filterPriority}
            onChange={handleFilterPriorityChange}
            options={[
              { value: 'All', label: 'All priorities' },
              ...PRIORITY_FILTER_OPTIONS.map(p => ({ value: p, label: p })),
            ]}
            minWidth={130}
          />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button onClick={() => setShowExport(true)} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${T.border}`, background: "#FFFFFF", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }} onMouseEnter={e => e.currentTarget.style.background = T.g50} onMouseLeave={e => e.currentTarget.style.background = "#fff"}><Icon name="download" size={15} color={T.muted} /></button>
          <button onClick={() => setShowFilters(f => !f)} style={{ width: 34, height: 34, borderRadius: 8, border: `1px solid ${showFilters ? T.active : T.border}`, background: showFilters ? T.g50 : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}><Icon name="filter" size={15} color={showFilters ? T.active : T.muted} /></button>
          {/* Column picker toggle */}
          <div style={{ position: "relative" }} ref={colPickerRef}>
            <button
              onClick={() => setShowColPicker(p => !p)}
              title="Show/hide columns"
              style={{ width: 34, height: 34, borderRadius: 8, border: `1.5px solid ${showColPicker ? T.active : T.border}`, background: showColPicker ? T.g50 : "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" }}
              onMouseEnter={e => { if (!showColPicker) e.currentTarget.style.background = T.g50; }}
              onMouseLeave={e => { if (!showColPicker) e.currentTarget.style.background = "#fff"; }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={showColPicker ? T.active : T.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
            {showColPicker && (
              <ColPicker
                visible={visibleCols}
                onChange={updateVisibleCols}
                onClose={() => setShowColPicker(false)}
              />
            )}
          </div>
        </div>
      </div>

      {showFilters && (<div style={{ background: T.g50, borderBottom: `1px solid ${T.border}`, padding: "10px 24px", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", flexShrink: 0 }}><span style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>Status Filter:</span>{statuses.map(s => (<button key={s} onClick={() => { setFilterStatus(s); if (s !== 'All') setUrlFilters(f => ({ ...f, view: undefined })); }} style={{ padding: "5px 14px", borderRadius: 99, border: `1px solid ${filterStatus === s ? T.active : T.border}`, background: filterStatus === s ? T.active : "#fff", color: filterStatus === s ? "#fff" : T.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s" }}>{s}</button>))}</div>)}
      {activeFilterLabels.length > 0 && (
        <div style={{ background: '#EFF6FF', borderBottom: `1px solid ${T.border}`, padding: '8px 24px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: T.active }}>From dashboard:</span>
          {activeFilterLabels.map(label => (
            <span key={label} style={{ fontSize: 12, fontWeight: 600, color: '#1A202C', background: '#fff', border: `1px solid ${T.border}`, borderRadius: 99, padding: '4px 12px' }}>{label}</span>
          ))}
          <button type="button" onClick={clearUrlFilters} style={{ marginLeft: 'auto', fontSize: 12, fontWeight: 600, color: T.active, background: 'none', border: 'none', cursor: 'pointer' }}>Clear filters</button>
        </div>
      )}

      {/* ── New / updated tickets banner (Freshservice-style) ───────────── */}
      {pendingTotal > 0 && (
        <div
          onClick={onRefreshTickets}
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            padding: "10px 20px",
            background: "#EFF6FF",
            color: "#1D4ED8",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
            userSelect: "none",
            borderBottom: "1px solid #BFDBFE",
            boxShadow: "0 2px 8px rgba(59,91,219,0.12)",
          }}
          title="Click to refresh the ticket list"
        >
          <span style={{
            width: 28, height: 28, borderRadius: "50%", background: "#3B5BDB",
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" />
              <polyline points="1 20 1 14 7 14" />
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
            </svg>
          </span>
          {pendingBannerText}
        </div>
      )}

      <div style={{ flex: 1, overflowY: "auto", overflowX: "auto", background: "#FFFFFF" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
            <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
              <tr style={{ background: "#F0F4FF" }}>
                {/* Fixed: checkbox */}
                <th style={{ ...thStyle, paddingLeft: 24, width: 44 }}>
                  <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${allSelected ? T.active : "#C8CDD6"}`, background: allSelected ? T.active : "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={toggleAll}>
                    {allSelected && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                  </div>
                </th>

                {col("requestNo")   && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("id")}>Request No. {sortKey === "id" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("description") && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("name")}>Problem Description {sortKey === "name" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("state")       && <th style={thStyle}>State</th>}
                {col("status")      && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("status")}>Current Status {sortKey === "status" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("sla")         && <th style={thStyle}>SLA</th>}
                {col("assignee")    && <th style={thStyle}>Assigned To</th>}
                {col("requestor")   && <th style={thStyle}>Requestor</th>}
                {col("dept")        && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("dept")}>Department {sortKey === "dept" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("priority")    && <th style={thStyle}>Priority</th>}
                {col("category")    && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("category")}>Category {sortKey === "category" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("subCategory") && <th style={thStyle}>Sub-Category</th>}
                {col("created")     && <th style={{ ...thStyle, cursor: "pointer" }} onClick={() => handleHeaderSort("created")}>Created Date {sortKey === "created" ? (sortDir === "asc" ? "↑" : "↓") : ""}</th>}
                {col("modified")    && <th style={thStyle}>Last Modified</th>}
                {col("orgUnit")     && <th style={thStyle}>Org Unit</th>}
                {col("headOfDept")  && <th style={thStyle}>Head of Dept</th>}
                {col("addlSubCat")  && <th style={thStyle}>Addl Sub-Cat</th>}
                {col("actionTaker") && <th style={thStyle}>Action Taker</th>}
                {col("at1Remarks")  && <th style={thStyle}>AT1 Remarks</th>}
                {col("at2")         && <th style={thStyle}>Action Taker 2</th>}
                {col("at3")         && <th style={thStyle}>Action Taker 3</th>}
                {col("endUser")     && <th style={thStyle}>End User</th>}
                {col("slaStatus")   && <th style={thStyle}>SLA Status</th>}
                {/* Fixed: actions */}
                <th style={{ ...thStyle, width: 40, paddingRight: 24 }}></th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((l, rowIdx) => {
                const dash = <span style={{ color: "#C8CDD6" }}>—</span>;
                const actualIdx = (safePage - 1) * perPage + rowIdx;
                const isSelected = selected.includes(l.id);
                const rowCompleted = isTerminalTicketStatus(l.status);
                const rowBg = isSelected ? "#EBF0FF" : rowCompleted ? "#F8FAFC" : rowIdx % 2 === 0 ? "#FFFFFF" : "#FAFBFF";
                const rowMuted = rowCompleted ? { opacity: 0.5 } : {};
                const listingState = getTicketListingState(l);
                const stateCfg = listingState ? TICKET_STATE_STYLES[listingState] : null;
                return (
                  <tr
                    key={l.id}
                    style={{ background: rowBg, transition: "background 0.1s, opacity 0.1s", boxShadow: `inset 0 -1px 0 ${T.border}`, ...rowMuted }}
                    onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = rowCompleted ? "#F1F5F9" : "#F0F4FF"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = rowBg; }}
                  >
                    <td style={{ ...tdStyle, paddingLeft: 24 }}>
                      <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${isSelected ? T.active : "#C8CDD6"}`, background: isSelected ? T.active : "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }} onClick={() => toggleOne(l.id)}>
                        {isSelected && <svg width="9" height="9" viewBox="0 0 12 12" fill="none"><polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="2" strokeLinecap="round"/></svg>}
                      </div>
                    </td>

                    {col("requestNo")   && <td style={{ ...tdStyle, whiteSpace: "nowrap", fontWeight: 700, color: T.active, cursor: "pointer" }} onClick={() => navigate('/tickets/' + l.id)}>{l.id}</td>}
                    {col("description") && <td style={{ ...tdStyle, maxWidth: 240, ...rowMuted }} onClick={() => navigate('/tickets/' + l.id)}><div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", fontWeight: 600, color: rowCompleted ? "#94A3B8" : T.active }}>{l.name}</div></td>}
                    {col("state")       && <td style={{ ...tdStyle, ...rowMuted, whiteSpace: "nowrap" }}>
                      {stateCfg ? (
                        <span style={{
                          display: "inline-block",
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "3px 10px",
                          borderRadius: 6,
                          background: stateCfg.bg,
                          color: stateCfg.color,
                          letterSpacing: "0.01em",
                        }}>
                          {stateCfg.label}
                        </span>
                      ) : dash}
                    </td>}
                    {col("status")      && <td style={{ ...tdStyle, ...rowMuted }}><InlineStatusDropdown ticketId={l._id} value={l.status} onChange={val => setListings(prev => prev.map(x => x._id === l._id ? { ...x, status: val } : x))} /></td>}
                    {col("sla")         && <td style={tdStyle}>
                      {(() => {
                        const due = l.resolutionDueAt ? new Date(l.resolutionDueAt) : null;
                        const now = Date.now();
                        // Recompute live status from due date
                        let liveStatus = l.slaStatus;
                        if (due) {
                          const remainMs = due.getTime() - now;
                          const totalMs  = due.getTime() - (l._created_at ? new Date(l._created_at).getTime() : now);
                          if (remainMs <= 0) liveStatus = 'breached';
                          else if (totalMs > 0 && remainMs / totalMs < 0.2) liveStatus = 'at_risk';
                          else liveStatus = 'within';
                        }
                        // Format time remaining / overdue
                        const fmtDur = ms => {
                          const abs = Math.abs(ms);
                          const d = Math.floor(abs / 86400000);
                          const h = Math.floor((abs % 86400000) / 3600000);
                          const m = Math.floor((abs % 3600000) / 60000);
                          if (d > 0) return `${d}d ${h}h`;
                          if (h > 0) return `${h}h ${m}m`;
                          return `${m}m`;
                        };
                        const remainMs = due ? due.getTime() - now : null;
                        const timeLabel = due
                          ? remainMs > 0 ? `${fmtDur(remainMs)} left` : `Overdue ${fmtDur(remainMs)}`
                          : null;
                        if (!liveStatus) return dash;
                        const cfg = {
                          breached: { bg: '#fee2e2', color: '#dc2626', icon: '✕', text: 'Breached' },
                          at_risk:  { bg: '#fffbeb', color: '#b45309', icon: '⚠', text: 'At Risk' },
                          within:   { bg: '#ECFDF5', color: '#059669', icon: '✓', text: 'Within SLA' },
                        }[liveStatus] || { bg: '#F8FAFC', color: '#64748B', icon: '', text: liveStatus };
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, background: cfg.bg, color: cfg.color, padding: '2px 7px', borderRadius: 99, whiteSpace: 'nowrap', display: 'inline-block' }}>{cfg.icon} {cfg.text}</span>
                            {timeLabel && <span style={{ fontSize: 10, color: remainMs > 0 ? '#64748B' : '#dc2626', fontWeight: remainMs <= 0 ? 700 : 400 }}>{timeLabel}</span>}
                          </div>
                        );
                      })()}
                    </td>}
                    {col("assignee")    && <td style={{ ...tdStyle, minWidth: 170 }}>
                      <InlineAssignedToDropdown
                        ticketId={l._id}
                        value={l.assignedAgent || (l.assignedTo ? { name: l.assignedTo } : null)}
                        groupName={(!l.assignedTo && !l.assignedAgent) ? (l.group || '') : ''}
                        onAssigned={(agent, group) => setListings(prev => prev.map(x => x._id === l._id ? {
                          ...x,
                          assignedTo: agent?.name || '',
                          assignedAgent: agent,
                          group: group?.name || (agent ? x.group : ''),
                          _assigned_group_id: group?.id || (agent ? x._assigned_group_id : null),
                          _assigned_to_id: agent?.id || null,
                          actionTaker1: agent?.name || '',
                        } : x))}
                      />
                    </td>}
                    {col("requestor")   && <td style={tdStyle}>{l.requestor || dash}</td>}
                    {col("dept")        && <td style={tdStyle}>
                      <InlineMasterDropdown
                        ticketId={l._id}
                        fieldType="department"
                        apiField="department"
                        value={l.dept}
                        onChange={val => setListings(prev => prev.map(x => x._id === l._id ? { ...x, dept: val } : x))}
                      />
                    </td>}
                    {col("priority")    && <td style={tdStyle}>
                      <InlinePriorityDropdown
                        ticketId={l._id}
                        value={l.priority}
                        onChange={val => setListings(prev => prev.map(x => x._id === l._id ? { ...x, priority: val } : x))}
                      />
                    </td>}
                    {col("category")    && <td style={tdStyle}>
                      <InlineMasterDropdown
                        ticketId={l._id}
                        fieldType="category"
                        apiField="category"
                        value={l.category}
                        onChange={val => setListings(prev => prev.map(x => x._id === l._id ? { ...x, category: val } : x))}
                      />
                    </td>}
                    {col("subCategory") && <td style={tdStyle}>
                      <InlineMasterDropdown
                        ticketId={l._id}
                        fieldType="sub_category"
                        apiField="sub_category"
                        value={l.subCategory}
                        parentValue={l.category}
                        disabled={!l.category}
                        onChange={val => setListings(prev => prev.map(x => x._id === l._id ? { ...x, subCategory: val } : x))}
                      />
                    </td>}
                    {col("created")     && <td style={{ ...tdStyle, fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>{l.created}</td>}
                    {col("modified")    && <td style={{ ...tdStyle, fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>{l.modified || dash}</td>}
                    {col("orgUnit")     && <td style={tdStyle}>{l.orgUnit || dash}</td>}
                    {col("headOfDept")  && <td style={tdStyle}>{l.headOfDept || dash}</td>}
                    {col("addlSubCat")  && <td style={tdStyle}>{l.additionalSubCategory || dash}</td>}
                    {col("actionTaker") && <td style={tdStyle}>{l.actionTaker || dash}</td>}
                    {col("at1Remarks")  && <td style={{ ...tdStyle, maxWidth: 160 }}>{l.actionTaker1Remarks || dash}</td>}
                    {col("at2")         && <td style={tdStyle}>{l.actionTaker2 || dash}</td>}
                    {col("at3")         && <td style={tdStyle}>{l.actionTaker3 || dash}</td>}
                    {col("endUser")     && <td style={tdStyle}>{l.endUser || dash}</td>}
                    {col("slaStatus")   && <td style={tdStyle}>{l.slaStatus ? <span style={{ fontSize: 11, fontWeight: 700, textTransform: "capitalize" }}>{l.slaStatus}</span> : dash}</td>}
                    <td style={{ ...tdStyle, paddingRight: 24 }}>
                      <button onClick={() => navigate('/tickets/' + l.id)} style={{ width: 28, height: 28, borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Icon name="eye" size={15} color={T.muted} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "80px 0", background: "#FFFFFF" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🎫</div>
              <div style={{ color: "#1A202C", fontSize: 15, fontWeight: 700 }}>No service requests found</div>
              <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>Try adjusting your search or filters</div>
            </div>
          )}
      </div>

      {/* ── Pagination bar ── */}
      <div style={{ flexShrink: 0, borderTop: `1px solid ${T.border}`, background: "#fff", padding: "0 24px", height: 48, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        {/* Left — rows per page */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 12, color: T.muted, fontWeight: 500 }}>Rows per page:</span>
          <select
            value={perPage}
            onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}
            style={{ height: 30, padding: "0 8px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, color: "#1A202C", background: "#fff", cursor: "pointer", outline: "none" }}
          >
            {[5, 10, 30, 50, 100, 500].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span style={{ fontSize: 12, color: T.muted }}>
            {filtered.length > 0
              ? `${(safePage - 1) * perPage + 1}–${Math.min(safePage * perPage, filtered.length)} of ${filtered.length} tickets`
              : "0 tickets"}
          </span>
        </div>
        {/* Right — page navigation */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <button onClick={() => setPage(1)} disabled={safePage === 1} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: safePage === 1 ? T.g50 : "#fff", color: safePage === 1 ? T.muted : "#1A202C", fontSize: 13, cursor: safePage === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>«</button>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: safePage === 1 ? T.g50 : "#fff", color: safePage === 1 ? T.muted : "#1A202C", fontSize: 13, cursor: safePage === 1 ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>‹</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
            .reduce((acc, p, idx, arr) => { if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...'); acc.push(p); return acc; }, [])
            .map((p, idx) => p === '...' ? (
              <span key={`d${idx}`} style={{ width: 28, textAlign: "center", fontSize: 12, color: T.muted }}>…</span>
            ) : (
              <button key={p} onClick={() => setPage(p)} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${safePage === p ? T.active : T.border}`, background: safePage === p ? T.active : "#fff", color: safePage === p ? "#fff" : "#1A202C", fontSize: 12, fontWeight: safePage === p ? 700 : 400, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>{p}</button>
            ))}
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: safePage === totalPages ? T.g50 : "#fff", color: safePage === totalPages ? T.muted : "#1A202C", fontSize: 13, cursor: safePage === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>›</button>
          <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} style={{ width: 28, height: 28, borderRadius: 6, border: `1px solid ${T.border}`, background: safePage === totalPages ? T.g50 : "#fff", color: safePage === totalPages ? T.muted : "#1A202C", fontSize: 13, cursor: safePage === totalPages ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>»</button>
        </div>
      </div>

      {showCreate && <CreateTicketModal onClose={() => setShowCreate(false)} onSave={handleCreate} />}
      {editListing && <ListingModal listing={editListing} onClose={() => setEditListing(null)} onSave={handleSaveEdit} />}
      {showExport && <ExportModal listings={filtered} onClose={() => setShowExport(false)} />}
      {detailListing && <ListingDetailPage listing={detailListing} allListings={filtered} onClose={() => navigate('/tickets')} onEdit={l => { handleEdit(l); }} onDelete={id => { handleDelete(id); }} onUpdate={updated => { setListings(ls => ls.map(l => l.id === updated.id ? updated : l)); }} />}
    </div>
  );
}
